import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, doc, updateDoc, getDoc, setDoc, deleteDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { Logo } from '../components/Logo';
import BarcodeScanner from '../components/BarcodeScanner';
import { Camera } from 'lucide-react';

// ==================== CONFIGURATION ====================
const LIBRARY_CONFIGS = {
  libraryOfCongress: {
    name: 'Library of Congress',
    endpoint: 'https://www.loc.gov/books/',
    sruEndpoint: 'https://lx2.loc.gov/sru/DB=LCDB/',
    enabled: true,
    priority: 1,
    free: true
  },
  openLibrary: {
    name: 'Open Library',
    endpoint: 'https://openlibrary.org/search.json',
    workEndpoint: 'https://openlibrary.org/api/books',
    enabled: true,
    priority: 2,
    free: true
  },
  googleBooks: {
    name: 'Google Books',
    endpoint: 'https://www.googleapis.com/books/v1/volumes',
    enabled: true,
    priority: 3,
    free: true
  }
};

// Updated locations - removed generic "MS"
const LOCATIONS = [
  'Non-Fiction', 'Fiction', 'MS Fiction', 'MS Non-Fiction',
  'MS Quick', 'Graphic Novels', 'MS Graphic Novels', 'MS GN Non-Fiction',
  'Easy', 'Easy Reader', 'Early Non-Fiction', 'Board Books', 'Reference', 'Spanish'
];

// Easy Reader subcategories
const EASY_READER_SUBCATEGORIES = ['ER1', 'ER2', 'ER3', 'ER4', 'EM'];

const PHYSICAL_FORMATS = [
  { value: 'Print', label: 'Book (Print)' },
  { value: 'Audio', label: 'Audiobook' },
  { value: 'Digital', label: 'Digital/eBook' },
  { value: 'DVD', label: 'DVD' },
  { value: 'Kit', label: 'Kit/Mixed Media' }
];

// Awards (book-level, goes to 500 field)
const AVAILABLE_AWARDS = [
  'Lux Libris Award nominee'
];

// ==================== HELPER FUNCTIONS ====================

// Convert text to proper Title Case (small words stay lowercase unless first)
const toTitleCase = (str) => {
  if (!str) return '';
  
  // Words that should stay lowercase (unless first word or after colon)
  const smallWords = new Set([
    'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 
    'on', 'or', 'so', 'the', 'to', 'up', 'yet', 'is', 'be', 'if', 'than'
  ]);
  
  // Track if previous character was a colon (next word should be capitalized)
  let afterColon = false;
  
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      // Check if word ends with colon for the next iteration
      const endsWithColon = word.endsWith(':');
      const cleanWord = word.replace(/:$/, ''); // Remove trailing colon for processing
      
      let result;
      // Always capitalize first word, word after colon, or if not a small word
      if (index === 0 || afterColon || !smallWords.has(cleanWord)) {
        result = cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1);
      } else {
        result = cleanWord;
      }
      
      // Update afterColon for next word
      afterColon = endsWithColon;
      
      // Add colon back if it was there
      return endsWithColon ? result + ':' : result;
    })
    .join(' ');
};

// Get default audience values based on location when data is missing
const getDefaultAudienceByLocation = (location) => {
  const defaults = {
    // Easy / Early Non-Fiction: PreK-2
    'Easy': {
      targetAudience: 'PreK-2',
      ageRange: '4-7 years',
      recommendedAge: 'Ages 4-7',
      lexileLevel: 'AD100L-500L'
    },
    'Early Non-Fiction': {
      targetAudience: 'PreK-2',
      ageRange: '4-7 years',
      recommendedAge: 'Ages 4-7',
      lexileLevel: 'AD100L-500L'
    },
    'Easy Reader': {
      targetAudience: 'PreK-2',
      ageRange: '4-7 years',
      recommendedAge: 'Ages 4-7',
      lexileLevel: 'AD100L-500L'
    },
    'Board Books': {
      targetAudience: 'PreK-1',
      ageRange: '0-4 years',
      recommendedAge: 'Ages 0-4',
      lexileLevel: 'AD100L-300L'
    },
    // Elementary Fiction/Non-Fiction/GN: Grades 2-5
    'Fiction': {
      targetAudience: '2-5',
      ageRange: '7-10 years',
      recommendedAge: 'Ages 7-10',
      lexileLevel: '500L-800L'
    },
    'Non-Fiction': {
      targetAudience: '2-5',
      ageRange: '7-10 years',
      recommendedAge: 'Ages 7-10',
      lexileLevel: '500L-800L'
    },
    'Graphic Novels': {
      targetAudience: '2-5',
      ageRange: '7-10 years',
      recommendedAge: 'Ages 7-10',
      lexileLevel: '500L-800L'
    },
    // Spanish: Grades 4-8
    'Spanish': {
      targetAudience: '4-8',
      ageRange: '9-14 years',
      recommendedAge: 'Ages 9-14',
      lexileLevel: '600L-1100L'
    },
    // Reference: Grades 5-8
    'Reference': {
      targetAudience: '5-8',
      ageRange: '10-14 years',
      recommendedAge: 'Ages 10-14',
      lexileLevel: '700L-1100L'
    },
    // Middle School: Grades 6-8
    'MS Fiction': {
      targetAudience: '6-8',
      ageRange: '11-14 years',
      recommendedAge: 'Ages 11-14',
      lexileLevel: '800L-1100L'
    },
    'MS Non-Fiction': {
      targetAudience: '6-8',
      ageRange: '11-14 years',
      recommendedAge: 'Ages 11-14',
      lexileLevel: '800L-1100L'
    },
    'MS Quick': {
      targetAudience: '6-8',
      ageRange: '11-14 years',
      recommendedAge: 'Ages 11-14',
      lexileLevel: '800L-1100L'
    },
    'MS Graphic Novels': {
      targetAudience: '6-8',
      ageRange: '11-14 years',
      recommendedAge: 'Ages 11-14',
      lexileLevel: '800L-1100L'
    },
    'MS GN Non-Fiction': {
      targetAudience: '6-8',
      ageRange: '11-14 years',
      recommendedAge: 'Ages 11-14',
      lexileLevel: '800L-1100L'
    }
  };
  
  return defaults[location] || {
    targetAudience: '2-5',
    ageRange: '7-10 years',
    recommendedAge: 'Ages 7-10',
    lexileLevel: '500L-800L'
  };
};

// ==================== LIBRARY SEARCH CLASS ====================
class MultiLibrarySearch {
  constructor(settings = {}) {
    this.libraries = { ...LIBRARY_CONFIGS };
    this.selectedLibraries = settings.selectedLibraries || ['libraryOfCongress', 'openLibrary', 'googleBooks'];
    this.apiKeys = settings.apiKeys || {};
    this.debugMode = true;
  }

  async searchAllLibraries(searchData) {
    const { isbn, title, authorFirst, authorLast } = searchData;
    const searchPromises = [];
    const results = {
      sources: {},
      merged: {},
      errors: [],
      successfulLibraries: []
    };

    const searchQuery = {
      isbn: isbn?.replace(/[-\s]/g, ''),
      title: title?.trim(),
      author: `${authorFirst} ${authorLast}`.trim()
    };

    console.log('🔍 Starting multi-library search:', searchQuery);

    for (const libraryKey of this.selectedLibraries) {
      if (this.libraries[libraryKey]?.enabled) {
        console.log(`📖 Queuing search for ${this.libraries[libraryKey].name}...`);
        searchPromises.push(
          this.searchLibrary(libraryKey, searchQuery)
            .then(data => {
              if (data) {
                console.log(`✅ ${this.libraries[libraryKey].name}: Found data`);
                results.successfulLibraries.push(this.libraries[libraryKey].name);
              } else {
                console.log(`⚠️ ${this.libraries[libraryKey].name}: No data found`);
              }
              return { library: libraryKey, data, success: !!data };
            })
            .catch(error => {
              console.error(`❌ ${this.libraries[libraryKey].name} failed:`, error.message);
              return { 
                library: libraryKey, 
                error: error.message, 
                success: false 
              };
            })
        );
      }
    }

    const searchResults = await Promise.all(searchPromises);

    searchResults.forEach(result => {
      if (result.success && result.data) {
        results.sources[result.library] = result.data;
      } else if (!result.success) {
        results.errors.push({ library: result.library, error: result.error });
      }
    });

    results.merged = this.mergeLibraryData(results.sources);
    
    console.log(`📊 Search complete: ${Object.keys(results.sources).length} libraries returned data`);
    if (results.successfulLibraries.length > 0) {
      console.log(`✅ Successful libraries: ${results.successfulLibraries.join(', ')}`);
    }
    
    return results;
  }

  async searchLibrary(libraryKey, searchQuery) {
    switch (libraryKey) {
      case 'libraryOfCongress':
        return this.searchLoC(searchQuery);
      case 'openLibrary':
        return this.searchOpenLibrary(searchQuery);
      case 'googleBooks':
        return this.searchGoogleBooks(searchQuery);
      default:
        throw new Error(`Library ${libraryKey} not implemented`);
    }
  }

  async searchLoC(query) {
    try {
      let searchClause = '';
      
      if (query.isbn) {
        const cleanIsbn = query.isbn.replace(/[-\s]/g, '');
        searchClause = `bath.isbn=${cleanIsbn}`;
      } else if (query.title) {
        searchClause = `dc.title all "${query.title}"`;
      } else {
        return null;
      }

      const url = `https://heimdl-marc-server.vercel.app/api/loc-proxy?query=${searchClause}`;
      
      console.log('🏛️ LoC query:', searchClause);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.log('🏛️ LoC: No results or error');
        return null;
      }
      
      const text = await response.text();
      console.log('🏛️ LoC response length:', text.length);
      
      if (text.length < 200) {
        console.log('🏛️ LoC: Response too short, likely no results');
        return null;
      }
      
      const parsedData = this.parseLoCResponse(text);
      return parsedData;
    } catch (error) {
      console.error('LoC search error:', error);
      return null;
    }
  }

  parseLoCResponse(xmlText) {
  try {
    console.log('🏛️ LoC response preview:', xmlText.substring(0, 300));
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    
    const diagnostic = xmlDoc.querySelector('diagnostic');
    if (diagnostic) {
      console.log('🏛️ LoC diagnostic:', diagnostic.textContent);
      return null;
    }
    
    // Check for MODS FIRST (before the wrapper 'record' element)
    const mods = xmlDoc.querySelector('mods');
    if (mods) {
      console.log('🏛️ Found MODS record');
      return this.parseMODSRecord(mods);
    }
    
    // Only check for MARCXML if MODS not found
    const record = xmlDoc.querySelector('record');
    if (record && !xmlDoc.querySelector('mods')) {
      console.log('🏛️ Found MARCXML record');
      return this.parseMARCXML(record);
    }
    
    console.log('🏛️ No valid record found in response');
    return null;
  } catch (error) {
    console.error('Error parsing LoC response:', error);
    return null;
  }
}

  parseMARCXML(record) {
    const getDatafield = (tag, code) => {
      const field = record.querySelector(`datafield[tag="${tag}"]`);
      if (field && code) {
        const subfield = field.querySelector(`subfield[code="${code}"]`);
        return subfield?.textContent?.trim() || '';
      }
      return field;
    };
    
    const getAllDatafields = (tag, code) => {
      const results = [];
      record.querySelectorAll(`datafield[tag="${tag}"]`).forEach(field => {
        if (code) {
          const subfield = field.querySelector(`subfield[code="${code}"]`);
          if (subfield) results.push(subfield.textContent.trim());
        } else {
          results.push(field);
        }
      });
      return results;
    };
    
    const getControlfield = (tag) => {
      const field = record.querySelector(`controlfield[tag="${tag}"]`);
      return field?.textContent?.trim() || '';
    };
    
    let isbn = getDatafield('020', 'a');
    if (isbn) {
      isbn = isbn.split(/[\s(]/)[0];
    }
    
    let author = getDatafield('100', 'a');
    if (author) {
      author = author.replace(/\.$/, '');
    }
    
    const field245 = record.querySelector('datafield[tag="245"]');
    let title = '';
    let subtitle = '';
    if (field245) {
      const titleA = field245.querySelector('subfield[code="a"]');
      const titleB = field245.querySelector('subfield[code="b"]');
      if (titleA) title = titleA.textContent.trim().replace(/\/$/, '').trim();
      if (titleB) subtitle = titleB.textContent.trim().replace(/\/$/, '').trim();
    }
    
    let publisher = getDatafield('260', 'b') || getDatafield('264', 'b');
    let publishDate = getDatafield('260', 'c') || getDatafield('264', 'c');
    
    if (publisher) publisher = publisher.replace(/[,:]$/, '').trim();
    if (publishDate) {
      const yearMatch = publishDate.match(/\d{4}/);
      if (yearMatch) publishDate = yearMatch[0];
    }
    
    const pages = getDatafield('300', 'a');
    const subjects = getAllDatafields('650', 'a');
    const genres = getAllDatafields('655', 'a');
    
    let dewey = getDatafield('082', 'a');
    if (dewey) {
      dewey = dewey.split(/[\s\/]/)[0];
    }
    
    const field008 = getControlfield('008');
    let language = '';
    if (field008 && field008.length > 38) {
      language = field008.substring(35, 38);
    }
    
    const data = {
      title: title || '',
      subtitle: subtitle || '',
      author: author || '',
      publisher: publisher || '',
      publishDate: publishDate || '',
      isbn13: isbn && isbn.length === 13 ? isbn : '',
      isbn10: isbn && isbn.length === 10 ? isbn : '',
      isbn: isbn || '',
      pages: pages || '',
      subjects: subjects,
      genres: genres,
      deweyDecimal: dewey || '',
      language: language || 'eng',
      source: 'Library of Congress'
    };
    
    return data.title ? data : null;
  }

  parseMODSRecord(mods) {
  const getElementText = (selector) => {
    const element = mods.querySelector(selector);
    return element?.textContent?.trim() || '';
  };

  const getAllElements = (selector) => {
    const elements = mods.querySelectorAll(selector);
    return Array.from(elements).map(el => el.textContent.trim());
  };

  // NEW: Parse target audience and convert ages to grades
  const rawAudience = getElementText('targetAudience');
  let targetAudience = '';
  let ageRange = '';
  let recommendedAge = '';
  
  if (rawAudience) {
    // Extract age range: "Ages 10-14 Candlewick Press" → "10-14"
    const ageMatch = rawAudience.match(/Ages?\s+(\d+)\s*[-–]\s*(\d+)/i);
    if (ageMatch) {
      const minAge = parseInt(ageMatch[1]);
      const maxAge = parseInt(ageMatch[2]);
      
      ageRange = `${minAge}-${maxAge} years`;
      recommendedAge = `Ages ${minAge}-${maxAge}`;
      
      // Convert ages to grade levels
      // Ages 5-7 = K-2, Ages 8-10 = 3-5, Ages 11-14 = 6-8, etc.
      let minGrade = Math.max(0, Math.floor((minAge - 5) / 3) * 3);
      let maxGrade = Math.max(2, Math.ceil((maxAge - 5) / 3) * 3 - 1);
      
      // Adjust for typical age-to-grade mapping
      if (minAge <= 7) minGrade = 0; // K-2
      else if (minAge <= 10) minGrade = 3; // 3-5
      else if (minAge <= 13) minGrade = 5; // 5-8 (middle school start)
      
      if (maxAge <= 7) maxGrade = 2;
      else if (maxAge <= 10) maxGrade = 5;
      else if (maxAge <= 14) maxGrade = 8;
      
      if (minGrade === 0) targetAudience = `K-${maxGrade}`;
      else targetAudience = `${minGrade}-${maxGrade}`;
    }
  }

  // NEW: Get DDC classification specifically (not LCC)
  let deweyDecimal = '';
  const classifications = mods.querySelectorAll('classification');
  classifications.forEach(classEl => {
    const authority = classEl.getAttribute('authority');
    if (authority === 'ddc') {
      deweyDecimal = classEl.textContent.trim();
    }
  });

  const data = {
    title: getElementText('titleInfo > title'),
    subtitle: getElementText('titleInfo > subTitle'),
    author: getElementText('name > namePart'),
    publisher: getElementText('originInfo > publisher'),
    publishDate: getElementText('originInfo > dateIssued'),
    edition: getElementText('originInfo > edition'),  // NEW: Edition statement
    isbn: getElementText('identifier[type="isbn"]'),
    lccn: getElementText('identifier[type="lccn"]'),  // NEW: Library of Congress Control Number
    pages: getElementText('physicalDescription > extent'),
    subjects: getAllElements('subject > topic'),
    genres: getAllElements('genre'),
    targetAudience: targetAudience,  // Grade level: "5-8"
    ageRange: ageRange,               // Age range: "10-14 years"
    recommendedAge: recommendedAge,   // Display format: "Ages 10-14"
    deweyDecimal: deweyDecimal,      // Now gets DDC specifically
    language: getElementText('language > languageTerm'),
    description: getElementText('abstract'),
    source: 'Library of Congress'
  };

  return data.title ? data : null;
}

  async searchOpenLibrary(query) {
    try {
      console.log('📖 Searching Open Library...');
      
      if (query.isbn) {
        const url = `${this.libraries.openLibrary.workEndpoint}?bibkeys=ISBN:${query.isbn}&format=json&jscmd=data`;
        console.log('📖 Open Library URL:', url);
        
        const response = await fetch(url);
        const data = await response.json();
        
        const bookKey = `ISBN:${query.isbn}`;
        if (data[bookKey]) {
          console.log('📖 Open Library: Found by ISBN');
          return this.parseOpenLibraryBook(data[bookKey]);
        }
      } else {
        const params = new URLSearchParams();
        if (query.title) params.append('title', query.title);
        if (query.author) params.append('author', query.author);
        params.append('limit', '1');
        
        const url = `${this.libraries.openLibrary.endpoint}?${params}`;
        console.log('📖 Open Library search URL:', url);
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.docs && data.docs[0]) {
          console.log('📖 Open Library: Found by title/author');
          return this.parseOpenLibrarySearch(data.docs[0]);
        }
      }
      
      console.log('📖 Open Library: No results');
      return null;
    } catch (error) {
      console.error('Open Library search error:', error);
      return null;
    }
  }

  parseOpenLibraryBook(book) {
    // Open Library book API includes classifications.dewey_decimal_class
    let deweyDecimal = '';
    if (book.classifications?.dewey_decimal_class?.length > 0) {
      deweyDecimal = book.classifications.dewey_decimal_class[0].split(/[\s\/]/)[0];
    }

    return {
      title: book.title,
      subtitle: book.subtitle,
      author: book.authors?.[0]?.name,
      coAuthors: book.authors?.slice(1).map(a => a.name),
      publisher: book.publishers?.[0]?.name,
      publishDate: book.publish_date,
      isbn13: book.identifiers?.isbn_13?.[0],
      isbn10: book.identifiers?.isbn_10?.[0],
      pages: book.number_of_pages,
      subjects: book.subjects?.map(s => s.name),
      deweyDecimal: deweyDecimal,
      coverUrl: book.cover?.large || book.cover?.medium,
      source: 'Open Library'
    };
  }

  parseOpenLibrarySearch(doc) {
    // Open Library search results include ddc (Dewey Decimal Classification) as an array
    let deweyDecimal = '';
    if (doc.ddc?.length > 0) {
      deweyDecimal = doc.ddc[0].split(/[\s\/]/)[0];
    }

    return {
      title: doc.title,
      subtitle: doc.subtitle,
      author: doc.author_name?.[0],
      coAuthors: doc.author_name?.slice(1),
      publisher: doc.publisher?.[0],
      publishDate: doc.first_publish_year?.toString(),
      isbn: doc.isbn?.[0],
      pages: doc.number_of_pages_median,
      subjects: doc.subject,
      deweyDecimal: deweyDecimal,
      coverUrl: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-L.jpg` : null,
      source: 'Open Library'
    };
  }

  async searchGoogleBooks(query) {
    try {
      console.log('📚 Searching Google Books...');
      
      let searchQuery = '';
      if (query.isbn) {
        searchQuery = `isbn:${query.isbn}`;
      } else {
        const parts = [];
        if (query.title) parts.push(`intitle:${query.title}`);
        if (query.author) parts.push(`inauthor:${query.author}`);
        searchQuery = parts.join(' ');
      }
      
      const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
      const url = `${this.libraries.googleBooks.endpoint}?q=${encodeURIComponent(searchQuery)}&maxResults=1${apiKey ? `&key=${apiKey}` : ''}`;
      console.log('📚 Google Books URL:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.items && data.items[0]) {
        console.log('📚 Google Books: Found result');
        return this.parseGoogleBooksData(data.items[0]);
      }
      
      console.log('📚 Google Books: No results');
      return null;
    } catch (error) {
      console.error('Google Books search error:', error);
      return null;
    }
  }

  parseGoogleBooksData(item) {
    const volume = item.volumeInfo;
    const sale = item.saleInfo;
    
    return {
      title: volume.title,
      subtitle: volume.subtitle,
      author: volume.authors?.[0],
      coAuthors: volume.authors?.slice(1),
      publisher: volume.publisher,
      publishDate: volume.publishedDate,
      isbn13: volume.industryIdentifiers?.find(id => id.type === 'ISBN_13')?.identifier,
      isbn10: volume.industryIdentifiers?.find(id => id.type === 'ISBN_10')?.identifier,
      pages: volume.pageCount,
      subjects: volume.categories,
      description: volume.description,
      language: volume.language,
      coverUrl: volume.imageLinks?.thumbnail || volume.imageLinks?.smallThumbnail,
      listPrice: sale?.listPrice?.amount,
      retailPrice: sale?.retailPrice?.amount,
      maturityRating: volume.maturityRating,
      source: 'Google Books'
    };
  }

  mergeLibraryData(sources) {
    const merged = {
      title: '',
      subtitle: '',
      author: '',
      coAuthors: [],
      publisher: '',
      publishDate: '',
      isbn13: '',
      isbn10: '',
      pages: '',
      subjects: [],
      genres: [],
      description: '',
      deweyDecimal: '',
      listPrice: null,
      coverUrl: '',
      sources: [],
      confidence: 0
    };

    const priorityOrder = ['libraryOfCongress', 'openLibrary', 'googleBooks'];
    
    priorityOrder.forEach(libraryKey => {
      const data = sources[libraryKey];
      if (!data) return;
      
      merged.sources.push(data.source || libraryKey);
      
      if (!merged.title && data.title) merged.title = data.title;
      if (!merged.subtitle && data.subtitle) merged.subtitle = data.subtitle;
      if (!merged.author && data.author) merged.author = data.author;
      if (!merged.publisher && data.publisher) merged.publisher = data.publisher;
      if (!merged.publishDate && data.publishDate) merged.publishDate = data.publishDate;
      if (!merged.isbn13 && data.isbn13) merged.isbn13 = data.isbn13;
      if (!merged.isbn10 && data.isbn10) merged.isbn10 = data.isbn10;
      if (!merged.pages && data.pages) merged.pages = data.pages;
      if (!merged.deweyDecimal && data.deweyDecimal) merged.deweyDecimal = data.deweyDecimal;
      
      if (data.coAuthors?.length > 0) {
        merged.coAuthors = [...new Set([...merged.coAuthors, ...data.coAuthors])];
      }
      
      if (data.subjects?.length > 0) {
        merged.subjects = [...new Set([...merged.subjects, ...data.subjects])];
      }
      
      if (data.genres?.length > 0) {
        merged.genres = [...new Set([...merged.genres, ...data.genres])];
      }
      
      if (data.description && data.description.length > merged.description.length) {
        merged.description = data.description;
      }
      
      if (data.listPrice) merged.listPrice = data.listPrice;
      
      if (data.coverUrl && (!merged.coverUrl || data.source === 'Google Books')) {
        merged.coverUrl = data.coverUrl;
      }
    });
    
    merged.confidence = Math.min(100, merged.sources.length * 33);
    
    return merged;
  }
}

// ==================== MAIN COMPONENT ====================
function ForgeOfOdin() {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  
  // Search state
  const [searchMode, setSearchMode] = useState('isbn');
  const [currentIsbn, setCurrentIsbn] = useState('');
  const [searchTitle, setSearchTitle] = useState('');
  const [authorFirst, setAuthorFirst] = useState('');
  const [authorLast, setAuthorLast] = useState('');
  const [amazonDetails, setAmazonDetails] = useState('');
  const [showAmazonDetails, setShowAmazonDetails] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // Book management
  const [batch, setBatch] = useState([]);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentBarcode, setCurrentBarcode] = useState(125864);
  
  // Multi-library search state - always use all three libraries
  const [manualPrice, setManualPrice] = useState('');
  const [searchStats, setSearchStats] = useState('');

  // Ref for auto-scrolling batch list
  const batchEndRef = useRef(null);
  // Ref for barcode scanner auto-trigger
  const pendingScanIsbn = useRef(null);
  
  // UI state
  const [catalogMode, setCatalogMode] = useState('catalog'); // 'catalog' or 'addCopies'
  const [editingBookIndex, setEditingBookIndex] = useState(null);
  const [recatalogingBookIndex, setRecatalogingBookIndex] = useState(null);
  const [enrichingIndex, setEnrichingIndex] = useState(null);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allGenres, setAllGenres] = useState([]);
  
  // Settings - load from localStorage for persistence across navigation
  const OVERRIDE_STORAGE_KEY = `heimdl_overrides_${currentUser?.uid}`;
  
  const loadOverrideSettings = () => {
    try {
      const saved = localStorage.getItem(OVERRIDE_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading override settings:', e);
    }
    return null;
  };
  
  const savedOverrides = loadOverrideSettings();
  
  const [applyLocationToAll, setApplyLocationToAll] = useState(savedOverrides?.applyLocationToAll || false);
  const [globalLocation, setGlobalLocation] = useState(savedOverrides?.globalLocation || 'Fiction');
  const [applyPhysicalFormatToAll, setApplyPhysicalFormatToAll] = useState(savedOverrides?.applyPhysicalFormatToAll || false);
  const [globalPhysicalFormat, setGlobalPhysicalFormat] = useState(savedOverrides?.globalPhysicalFormat || 'Print');
  const [selectedPriceTier, setSelectedPriceTier] = useState(savedOverrides?.selectedPriceTier || null);
  const [selectedAwards, setSelectedAwards] = useState(savedOverrides?.selectedAwards || []);
  const [batchMode, setBatchMode] = useState(() => {
    try {
      return localStorage.getItem(`heimdl_batchMode_${currentUser?.uid}`) === 'true';
    } catch { return false; }
  });
  const [showCopyNotes, setShowCopyNotes] = useState(false);
  const [copyNoteOptions, setCopyNoteOptions] = useState({
    libraryPurchase: false,
    donation: false,
    donorName: '',
    damage: false,
    bindingType: '', // hardback, paperback, board book, bound
    tla: false,
    tlaYear: ''
  });
  const [contentFilterSettings, setContentFilterSettings] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [checkDuplicatesForThisBook, setCheckDuplicatesForThisBook] = useState(false);
  
  // Oversized modifier
  const [isOversized, setIsOversized] = useState(false);
  const [globalGN2, setGlobalGN2] = useState(false);
  const [globalEasyReaderSub, setGlobalEasyReaderSub] = useState('ER1');

  // Not found / Manual entry state
  const [notFound, setNotFound] = useState(false);
  const [manualEntryMode, setManualEntryMode] = useState(false);
  const [manualBookData, setManualBookData] = useState({
    title: '',
    subtitle: '',
    author: '',
    publisher: '',
    publishDate: '',
    pages: '',
    isbn13: '',
    description: '',
    ficOrNonfic: 'Fiction',
    deweyDecimal: '',
    location: 'Fiction',
    easyReaderSubcategory: 'ER1',
    subjects: [''],
    genres: [''],
    targetAudience: '',
    ageRange: '',
    lexileLevel: '',
    physicalFormat: 'Print',
    generalNotes: '',
    series: '',
    seriesNumber: '',
    subjectPerson: '',
    isGN2: false
  });

  // Add Copies mode state
  const [copyCallNumber, setCopyCallNumber] = useState('');
  const [copyTitle, setCopyTitle] = useState('');
  const [copyCount, setCopyCount] = useState(1);

  // Notes - awards are now in override settings above
  const [customNote, setCustomNote] = useState('');
  const [luxLibrisYear, setLuxLibrisYear] = useState('');

  // LocalStorage key
  const BATCH_STORAGE_KEY = `heimdl_batch_${currentUser?.uid}`;

  // Initialize multi-library search - always use all three libraries
  const multiLibrarySearch = new MultiLibrarySearch({
    selectedLibraries: ['libraryOfCongress', 'openLibrary', 'googleBooks'],
    apiKeys: {
      worldCat: process.env.REACT_APP_WORLDCAT_KEY
    }
  });

  // ==================== EFFECTS ====================
  
  // Persist batchMode toggle
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(`heimdl_batchMode_${currentUser.uid}`, batchMode.toString());
    }
  }, [currentUser, batchMode]);

  // Save override settings to localStorage only when batch mode is ON
  useEffect(() => {
    if (currentUser && batchMode) {
      const overrides = {
        applyLocationToAll,
        globalLocation,
        applyPhysicalFormatToAll,
        globalPhysicalFormat,
        selectedPriceTier
      };
      localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(overrides));
    } else if (currentUser && !batchMode) {
      localStorage.removeItem(OVERRIDE_STORAGE_KEY);
    }
  }, [currentUser, batchMode, applyLocationToAll, globalLocation, applyPhysicalFormatToAll, globalPhysicalFormat, selectedPriceTier]);

  useEffect(() => {
    const loadSettings = async () => {
      if (currentUser) {
        await loadContentFilterSettings();
        setSettingsLoaded(true);
      }
    };
    
    loadSettings();
    loadBatchFromStorage();
    loadFlaggedCount();
  }, [userData, currentUser]);

  // Real-time listener for approved books from Loki's Chamber
  useEffect(() => {
    if (!currentUser) return;

    const approvedRef = collection(db, 'users', currentUser.uid, 'approvedBooks');
    const processedDocIds = new Set(); // Track docs we've already handled

    const unsubscribe = onSnapshot(approvedRef, async (snapshot) => {
      if (snapshot.empty) return;

      const approvedBooks = [];
      const deletePromises = [];

      snapshot.forEach((docSnap) => {
        // Skip docs we've already processed (prevents re-adding during cascading snapshots)
        if (processedDocIds.has(docSnap.id)) return;
        processedDocIds.add(docSnap.id);

        const bookData = docSnap.data();
        bookData._approvedDocId = docSnap.id;
        approvedBooks.push(bookData);
        deletePromises.push(deleteDoc(docSnap.ref));
      });

      if (approvedBooks.length === 0) return;

      // Delete from Firebase FIRST to prevent race condition
      await Promise.all(deletePromises);

      // Then add to batch with duplicate check
      setBatch(prev => {
        const existingBarcodes = new Set(prev.map(b => b.barcode));
        const newBooks = approvedBooks.filter(b => !existingBarcodes.has(b.barcode));
        if (newBooks.length < approvedBooks.length) {
          console.log(`⚠️ Filtered out ${approvedBooks.length - newBooks.length} duplicate books`);
        }
        if (newBooks.length > 0) {
          console.log(`✅ Loaded ${newBooks.length} approved books from Loki's Chamber`);
        }
        return [...prev, ...newBooks];
      });
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Real-time barcode sync
  useEffect(() => {
    if (userData?.lastBarcodeUsed) {
      setCurrentBarcode(userData.lastBarcodeUsed + 1);
    }
  }, [userData?.lastBarcodeUsed]);

  // Periodic barcode refresh to catch updates from other sessions
  useEffect(() => {
    if (!currentUser) return;
    
    const refreshBarcode = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const lastUsed = userDoc.data().lastBarcodeUsed;
          if (lastUsed && lastUsed >= currentBarcode - 1) {
            setCurrentBarcode(lastUsed + 1);
          }
        }
      } catch (error) {
        console.error('Error refreshing barcode:', error);
      }
    };
    
    // Refresh every 5 seconds
    const interval = setInterval(refreshBarcode, 5000);
    
    // Also refresh when window gains focus
    const handleFocus = () => {
      refreshBarcode();
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [currentUser, currentBarcode]);

  useEffect(() => {
    if (currentUser && batch.length > 0) {
      saveBatchToStorage();
    }
  }, [batch, currentUser]);

  // Auto-scroll batch list to bottom when new books are added
  useEffect(() => {
    if (batch.length > 0 && batchEndRef.current) {
      batchEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [batch.length]);

  // Fetch all unique subjects and genres for autocomplete
  useEffect(() => {
    if (!currentUser) return;

    const fetchSubjectsAndGenres = async () => {
      try {
        const booksRef = collection(db, 'users', currentUser.uid, 'books');
        const booksSnapshot = await getDocs(booksRef);

        const subjectsSet = new Set();
        const genresSet = new Set();

        booksSnapshot.forEach(doc => {
          const book = doc.data();
          if (book.subjects) {
            book.subjects.forEach(subject => {
              if (subject && subject.trim()) {
                subjectsSet.add(subject.trim());
              }
            });
          }
          if (book.genres) {
            book.genres.forEach(genre => {
              if (genre && genre.trim()) {
                genresSet.add(genre.trim());
              }
            });
          }
        });

        setAllSubjects(Array.from(subjectsSet).sort());
        setAllGenres(Array.from(genresSet).sort());
      } catch (error) {
        console.error('Error fetching subjects/genres:', error);
      }
    };

    fetchSubjectsAndGenres();
  }, [currentUser]);

  // NEW: Manual barcode refresh button (optional enhancement)
  const refreshBarcodeNow = async () => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const lastUsed = userDoc.data().lastBarcodeUsed;
        setCurrentBarcode(lastUsed + 1);
        console.log(`🔄 Barcode refreshed: ${lastUsed + 1}`);
      }
    } catch (error) {
      console.error('Error refreshing barcode:', error);
    }
  };

  // ==================== HELPER FUNCTIONS ====================
  const loadFlaggedCount = async () => {
    if (!currentUser) return;
    
    try {
      const flaggedRef = collection(db, 'users', currentUser.uid, 'flaggedBooks');
      const flaggedSnapshot = await getDocs(flaggedRef);
      setFlaggedCount(flaggedSnapshot.size);
    } catch (error) {
      console.error('Error loading flagged count:', error);
      setFlaggedCount(0);
    }
  };

  const saveBatchToStorage = () => {
    try {
      localStorage.setItem(BATCH_STORAGE_KEY, JSON.stringify({
        batch: batch,
        currentBarcode: currentBarcode,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving batch:', error);
    }
  };

  const loadBatchFromStorage = () => {
    try {
      const savedBatch = localStorage.getItem(BATCH_STORAGE_KEY);
      
      if (savedBatch) {
        const batchData = JSON.parse(savedBatch);
        setBatch(batchData.batch || []);
        if (batchData.currentBarcode) {
          setCurrentBarcode(batchData.currentBarcode);
        }
      }
    } catch (error) {
      console.error('Error loading batch:', error);
    }
  };

  const clearBatchStorage = () => {
    try {
      localStorage.removeItem(BATCH_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing batch storage:', error);
    }
  };

  const loadContentFilterSettings = async () => {
    if (!currentUser) return;
    
    try {
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setContentFilterSettings({
          enableContentFiltering: settings.enableContentFiltering ?? true,
          flagOverGrade8: settings.flagOverGrade8 ?? true,
          flagSexualContent: settings.flagSexualContent ?? true,
          flagControversialTopics: settings.flagControversialTopics ?? true,
          flagViolence: settings.flagViolence ?? false,
          flagLanguage: settings.flagLanguage ?? false,
          enableDuplicateChecking: settings.enableDuplicateChecking ?? false,
          opacUrl: settings.opacUrl || '',
          opacUsername: settings.opacUsername || ''
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setContentFilterSettings({
        enableContentFiltering: true,
        flagOverGrade8: true,
        flagSexualContent: true,
        flagControversialTopics: true,
        flagViolence: false,
        flagLanguage: false,
        enableDuplicateChecking: false,
        opacUrl: '',
        opacUsername: ''
      });
    }
  };

  const checkOPACDuplicates = async (bookData, isbn) => {
    if (!settingsLoaded || !contentFilterSettings.enableDuplicateChecking || 
        !contentFilterSettings.opacUrl || !contentFilterSettings.opacUsername) {
      return { isDuplicate: false, duplicateInfo: null };
    }

    try {
      let searchQuery, searchType;
      
      if (isbn && isbn !== 'No ISBN') {
        searchQuery = isbn.replace(/[-\s]/g, '');
        searchType = 'isbn';
      } else {
        searchQuery = `${bookData.title} ${bookData.author}`.trim();
        searchType = 'title';
      }

      const functionUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/checkOPACDuplicate`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          opacUrl: contentFilterSettings.opacUrl,
          username: contentFilterSettings.opacUsername,
          searchQuery: searchQuery,
          searchType: searchType
        })
      });

      if (!response.ok) {
        throw new Error(`Cloud function error: ${response.status}`);
      }

      const result = await response.json();
      
      return {
        isDuplicate: result.isDuplicate || false,
        duplicateInfo: result.duplicateInfo || null
      };

    } catch (error) {
      console.error('OPAC duplicate check failed:', error);
      return { isDuplicate: false, duplicateInfo: null };
    }
  };

  // Parse Amazon product details
  const parseAmazonDetails = (amazonText) => {
    const details = {};
    
    // Extract title and author from the beginning (various formats)
    const lines = amazonText.split('\n').map(l => l.trim()).filter(l => l);
    
    // Try to find title - usually the first substantial line or line with "Hardcover/Paperback"
    const titleMatch = amazonText.match(/^(.+?)(?:Hardcover|Paperback|Kindle|Picture Book)/m);
    if (titleMatch) {
      let titleLine = titleMatch[1].trim();
      
      // Check if it has "by Author" pattern
      const byMatch = titleLine.match(/^(.+?)\s+by\s+(.+)$/i);
      if (byMatch) {
        details.title = byMatch[1].trim();
        let authorName = byMatch[2].trim().replace(/\(Author\)/gi, '').trim();
        
        // Format as Last, First
        const nameParts = authorName.split(' ');
        if (nameParts.length > 1) {
          const lastName = nameParts.pop();
          const firstName = nameParts.join(' ');
          details.author = `${lastName}, ${firstName}`;
          details.rawAuthor = authorName;
        } else {
          details.author = authorName;
          details.rawAuthor = authorName;
        }
      } else {
        details.title = titleLine;
      }
    }
    
    // Alternative: Look for author in "by X. Fang (Author)" format
    if (!details.author) {
      const authorLineMatch = amazonText.match(/by\s+([^\n(]+)(?:\s*\(Author\))?/i);
      if (authorLineMatch) {
        let authorName = authorLineMatch[1].trim();
        const nameParts = authorName.split(' ');
        if (nameParts.length > 1) {
          const lastName = nameParts.pop();
          const firstName = nameParts.join(' ');
          details.author = `${lastName}, ${firstName}`;
          details.rawAuthor = authorName;
        } else {
          details.author = authorName;
          details.rawAuthor = authorName;
        }
      }
    }
    
    // Extract publisher
    const publisherMatch = amazonText.match(/Publisher[:\s]+([^\n]+)/i);
    if (publisherMatch) details.publisher = publisherMatch[1].trim();
    
    // Extract publication date
    const pubDateMatch = amazonText.match(/Publication date[:\s]+([^\n]+)/i);
    if (pubDateMatch) {
      const dateStr = pubDateMatch[1].trim();
      const yearMatch = dateStr.match(/\d{4}/);
      if (yearMatch) details.publishDate = yearMatch[0];
    }
    
    // Extract language
    const langMatch = amazonText.match(/Language[:\s]+([^\n]+)/i);
    if (langMatch) {
      const lang = langMatch[1].trim().toLowerCase();
      if (lang.includes('english')) details.language = 'eng';
      else if (lang.includes('spanish')) details.language = 'spa';
      else if (lang.includes('french')) details.language = 'fre';
    }
    
    // Extract pages
    const pagesMatch = amazonText.match(/Print length[:\s]+(\d+)\s*pages/i);
    if (pagesMatch) details.pages = pagesMatch[1];
    
    // Extract ISBN-10
    const isbn10Match = amazonText.match(/ISBN-10[:\s]+(\d{10})/i);
    if (isbn10Match) details.isbn10 = isbn10Match[1];
    
    // Extract ISBN-13
    const isbn13Match = amazonText.match(/ISBN-13[:\s]+([\d-]+)/i);
    if (isbn13Match) details.isbn13 = isbn13Match[1].replace(/[-\s]/g, '');
    
    // Extract reading age - can be "3 - 8 years" or "3-8 years"
    const readingAgeMatch = amazonText.match(/Reading age[:\s]+(.+?)(?:,|from|$)/i);
    if (readingAgeMatch) {
      const ageText = readingAgeMatch[1].trim();
      details.ageRange = ageText;
      
      // Try to extract years for recommendedAge
      const yearsMatch = ageText.match(/(\d+)\s*[-–]\s*(\d+)\s*years?/i);
      if (yearsMatch) {
        details.recommendedAge = `Ages ${yearsMatch[1]}-${yearsMatch[2]}`;
      }
    }
    
    // Extract grade level - "Preschool - 3" or "K-5"
    const gradeLevelMatch = amazonText.match(/Grade level[:\s]+([^\n]+)/i);
    if (gradeLevelMatch) {
      let gradeText = gradeLevelMatch[1].trim();
      
      // Convert to standard format
      if (gradeText.toLowerCase().includes('preschool')) {
        if (gradeText.match(/preschool\s*[-–]\s*(\d+)/i)) {
          const grade = gradeText.match(/preschool\s*[-–]\s*(\d+)/i)[1];
          details.targetAudience = `K-${grade}`;
        } else {
          details.targetAudience = 'PreK-K';
        }
      } else {
        details.targetAudience = gradeText;
      }
    }
    
    // Extract dimensions for physical description
    const dimensionsMatch = amazonText.match(/Dimensions[:\s]+([\d.]+)\s*x\s*([\d.]+)\s*x\s*([\d.]+)\s*inches/i);
    if (dimensionsMatch && details.pages) {
      details.physicalDesc = `${details.pages} pages : ${dimensionsMatch[1]} x ${dimensionsMatch[3]} inches`;
    }
    
    // Extract weight for additional context
    const weightMatch = amazonText.match(/Item Weight[:\s]+([\d.]+)\s*pounds?/i);
    if (weightMatch && !details.physicalDesc) {
      details.physicalDesc = `${details.pages || 'Unknown'} pages`;
    }
    
    // Try to extract book description if present
    const descMatch = amazonText.match(/(?:WINNER|A hilarious|An American Library|Perfect for)(.{100,500})/i);
    if (descMatch) {
      details.description = descMatch[0].substring(0, 300).trim();
    }
    
    // Mark source
    details.source = 'Amazon (manually provided)';
    details.dataSources = ['Amazon'];
    
    console.log('📦 Parsed Amazon details:', details);
    return details;
  };

  // Lightweight content check - fires automatically on every book
  const searchBookContentCheck = async (searchData, existingData = {}) => {
    for (let attempt = 0; attempt <= 2; attempt++) {
      try {
        const title = existingData.title || searchData.title || '';
        const author = existingData.author || `${searchData.authorFirst || ''} ${searchData.authorLast || ''}`.trim();
        const isbn = existingData.isbn13 || existingData.isbn10 || searchData.isbn || '';

        const prompt = `You are a content reviewer for a PreK-8 Catholic school library. Analyze this book for content appropriateness ONLY.

Title: ${title}
Author: ${author}
${isbn ? `ISBN: ${isbn}` : ''}

Return ONLY the following JSON - no other fields:
{
  "contentFlags": {
    "overGrade8": true/false,
    "lgbtqContent": true/false,
    "matureThemes": true/false,
    "violence": true/false,
    "scaryContent": true/false,
    "religiousContent": true/false
  },
  "lgbtqDetails": "Detailed analysis if lgbtqContent is true, otherwise empty string",
  "lgbtqSeverity": "NONE | MINIMAL | SECONDARY | PROMINENT | CENTRAL",
  "subjects": ["Array of specific subject headings, e.g. Dinosaurs, World War II, Friendship, Dogs, Solar System - provide at least 3-5"],
  "genres": ["Array of genre classifications, e.g. Fantasy, Historical Fiction, Graphic Novel, Biography, Mystery - provide 1-3"],
  "forFansOf": ["Similar well-known book or series", "Comparable popular author or title"]
}`;

        const functionUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/analyzeBookWithAI`;

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `AI API error: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Content check failed');
        }

        console.log('🛡️ Content check complete');
        return result.bookData;

      } catch (error) {
        console.error(`Content check attempt ${attempt + 1} failed:`, error);
        if (attempt === 2) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  };

  // Rich enrichment call - fires on-demand when user clicks "Ask AI"
  const searchBookEnrichment = async (searchData, existingData = {}, location = '') => {
    for (let attempt = 0; attempt <= 2; attempt++) {
      try {
        const title = existingData.title || searchData.title || '';
        const author = existingData.author || `${searchData.authorFirst || ''} ${searchData.authorLast || ''}`.trim();
        const isbn = existingData.isbn13 || existingData.isbn10 || searchData.isbn || '';

        const prompt = `You are an expert school librarian cataloging books for a PreK-8 Catholic school library. Enrich this book's catalog record.

Known Information:
Title: ${title}
Author: ${author}
${isbn ? `ISBN: ${isbn}` : ''}
${existingData.publisher ? `Publisher: ${existingData.publisher}` : ''}
${location ? `Current Location: ${location}` : ''}
${existingData.deweyDecimal ? `Dewey (from library source): ${existingData.deweyDecimal}` : 'Dewey: NEEDED'}
${existingData.description ? `Description: ${existingData.description}` : ''}

This school uses these call number conventions:
- MS Quick (middle school quick readers, often biographies): Q [Dewey without decimal] [3-letter code of SUBJECT PERSON]
  Example: A biography about Beverly Cleary → Q 921 CLE (CLE = Cleary, the subject person)
  Example: A book about sharks → Q 597 SHA (non-biography uses author code)
- Non-Fiction: [Full Dewey] [3-letter author code]
- Fiction: FIC [3-letter author code]
- Easy Reader: [ER level] [3-letter author code]

For biographies (Dewey 920/921), the "subjectPerson" is the person the book is ABOUT, not the author who wrote it.

Return ONLY the following JSON:
{
  "deweyDecimal": "Dewey classification (e.g., 921, 813.54, 598.2)",
  "subjectPerson": "For biographies: Last, First of the person the book is ABOUT. Empty string if not a biography.",
  "series": "Series name if part of a series, empty string otherwise",
  "seriesNumber": "Volume/number in series (e.g., 3, 12), empty string if not applicable",
  "forFansOf": ["Similar book or series", "Comparable author"],
  "description": "2-3 sentence description appropriate for school library catalog",
  "ficOrNonfic": "Fiction | Non-Fiction",
  "suggestedLocation": "One of: Non-Fiction, Fiction, MS Fiction, MS Non-Fiction, MS Quick, Graphic Novels, MS Graphic Novels, MS GN Non-Fiction, Easy, Easy Reader, Early Non-Fiction, Reference, Spanish",
  "studyPrograms": ["Accelerated Reader", "Scholastic Reading Counts", etc. or empty array]
}`;

        const functionUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/analyzeBookWithAI`;

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `AI API error: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'AI enrichment failed');
        }

        console.log('🤖 AI enrichment complete');
        return result.bookData;

      } catch (error) {
        console.error(`AI enrichment attempt ${attempt + 1} failed:`, error);
        if (attempt === 2) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  };

  const mergeDataSources = (libraryData, aiData) => {
    const merged = { ...aiData };
    
    // Clear AI-generated audience fields - location defaults are more reliable
    // Library data can still override these if available
    delete merged.targetAudience;
    delete merged.ageRange;
    delete merged.recommendedAge;
    delete merged.lexileLevel;
    
    // Factual fields where library data takes precedence over AI
    const factualFields = ['title', 'subtitle', 'author', 'publisher', 'publishDate', 'isbn13', 'isbn10', 'pages', 'description', 'targetAudience', 'ageRange', 'recommendedAge', 'lexileLevel'];
    factualFields.forEach(field => {
      if (libraryData[field]) {
        merged[field] = libraryData[field];
      }
    });
    
    if (libraryData.subjects?.length > 0) {
      merged.subjects = [...new Set([...(merged.subjects || []), ...libraryData.subjects])];
    }

    if (libraryData.genres?.length > 0) {
      merged.genres = [...new Set([...(merged.genres || []), ...libraryData.genres])];
    }

    if (libraryData.coverUrl) {
      merged.coverUrl = libraryData.coverUrl;
    }
    
    if (libraryData.deweyDecimal) merged.deweyDecimal = libraryData.deweyDecimal;
    
    merged.confidence = libraryData.confidence || 0;
    merged.sources = libraryData.sources || [];
    
    return merged;
  };

  const determineLocation = (targetAudience, ficOrNonfic, genres = [], ageRange = '', readingLevel = '') => {
    const isGraphicNovel = genres.some(g => g.toLowerCase().includes('graphic'));
    
    if (isGraphicNovel) {
      if (targetAudience === '6-8' || targetAudience === '9-12' || targetAudience === 'Adult') {
        return 'MS Graphic Novels';
      } else {
        return 'Graphic Novels';
      }
    }
    
    if (targetAudience) {
      if (targetAudience === '3-7' || targetAudience === '4-7' || targetAudience === '2-6') {
        return ficOrNonfic === 'Fiction' ? 'Fiction' : 'Non-Fiction';
      }
      
      if (targetAudience === '5-8' || targetAudience === '6-9' || targetAudience === '7-9') {
        return ficOrNonfic === 'Fiction' ? 'MS Fiction' : 'MS Non-Fiction';
      }
      
      switch (targetAudience) {
        case 'K-2':
        case 'PreK-1':
        case 'K-1':
          if (ficOrNonfic === 'Non-Fiction' && readingLevel && parseFloat(readingLevel) > 2) {
            return 'Non-Fiction';
          }
          return 'Easy';
          
        case '2-5':
        case '3-5':
        case '2-4':
        case '3-4':
        case '4-5':
          return ficOrNonfic === 'Fiction' ? 'Fiction' : 'Non-Fiction';
          
        case '6-8':
        case '7-8':
        case '6-7':
          return ficOrNonfic === 'Fiction' ? 'MS Fiction' : 'MS Non-Fiction';
          
        case '9-12':
        case '10-12':
        case 'Adult':
          return ficOrNonfic === 'Fiction' ? 'MS Fiction' : 'MS Non-Fiction';
          
        default:
          const rangeMatch = targetAudience.match(/(\d+)[-–](\d+)/);
          if (rangeMatch) {
            const lowerGrade = parseInt(rangeMatch[1]);
            const upperGrade = parseInt(rangeMatch[2]);
            
            if (lowerGrade <= 3 && upperGrade <= 7) {
              return ficOrNonfic === 'Fiction' ? 'Fiction' : 'Non-Fiction';
            } else if (lowerGrade >= 5) {
              return ficOrNonfic === 'Fiction' ? 'MS Fiction' : 'MS Non-Fiction';
            } else if (lowerGrade <= 2) {
              return lowerGrade === 0 ? 'Easy' : (ficOrNonfic === 'Fiction' ? 'Fiction' : 'Non-Fiction');
            }
          }
          
          return ficOrNonfic === 'Fiction' ? 'Fiction' : 'Non-Fiction';
      }
    }
    
    if (readingLevel) {
      const rl = parseFloat(readingLevel);
      if (rl < 2.0) return 'Easy';
      else if (rl < 6.0) return ficOrNonfic === 'Fiction' ? 'Fiction' : 'Non-Fiction';
      else return ficOrNonfic === 'Fiction' ? 'MS Fiction' : 'MS Non-Fiction';
    }
    
    return ficOrNonfic === 'Fiction' ? 'Fiction' : 'Non-Fiction';
  };

  const checkContentFlags = (bookData, duplicateCheck = null) => {
    if (!contentFilterSettings.enableContentFiltering && !duplicateCheck?.isDuplicate) {
      return [];
    }
    
    const flags = [];
    const contentFlags = bookData.contentFlags || {};
    
    if (contentFilterSettings.enableContentFiltering) {
      if (contentFlags.lgbtqContent && contentFilterSettings.flagControversialTopics) {
        flags.push('LGBTQ Content');
      }
      
      if (contentFlags.overGrade8 && contentFilterSettings.flagOverGrade8) {
        flags.push('Over Grade 8');
      }
      
      if (contentFlags.matureThemes && contentFilterSettings.flagSexualContent) {
        flags.push('Mature Themes');
      }
      
      if (contentFlags.violence && contentFilterSettings.flagViolence) {
        flags.push('Violence');
      }
      
      if (contentFlags.scaryContent) {
        flags.push('Scary Content');
      }
      
      if (contentFlags.religiousContent && contentFilterSettings.flagControversialTopics) {
        flags.push('Religious Content');
      }
    }
    
    if (duplicateCheck?.isDuplicate) {
      flags.push('Potential Duplicate');
    }
    
    return flags;
  };

  const generateCallNumber = (location, dewey, author, oversized = false, easyReaderSubcategory = null, subjectPerson = null, isGN2 = false) => {
    const authorCode = author && author !== 'Unknown Author'
      ? author.split(',')[0].substring(0, 3).toUpperCase()
      : 'UNK';

    // Truncate dewey for Early Non-Fiction (remove decimal)
    const truncatedDewey = dewey ? dewey.split('.')[0] : '000';

    // For biographies (920/921), use the subject person's name instead of the author
    const quickCode = (() => {
      if (subjectPerson && (truncatedDewey === '921' || truncatedDewey === '920')) {
        return subjectPerson.split(',')[0].substring(0, 3).toUpperCase();
      }
      return authorCode;
    })();

    const rules = {
      'Non-Fiction': `${dewey || '000'} ${authorCode}`,
      'Fiction': `FIC ${authorCode}`,
      'MS Fiction': `MS FIC ${authorCode}`,
      'MS Non-Fiction': `MS ${dewey || '000'} ${authorCode}`,
      'MS Quick': `Q ${truncatedDewey} ${quickCode}`,
      'Graphic Novels': `${isGN2 ? 'GN2' : 'GN'} ${authorCode}`,
      'MS Graphic Novels': `MS GN ${authorCode}`,
      'MS GN Non-Fiction': `MS GN ${dewey || '000'} ${authorCode}`,
      'Easy': `E ${authorCode}`,
      'Easy Reader': `${easyReaderSubcategory || 'ER1'} ${authorCode}`,
      'Early Non-Fiction': `E ${truncatedDewey} ${authorCode}`,
      'Board Books': `B ${authorCode}`,
      'Reference': `REF ${dewey || '000'} ${authorCode}`,
    'Spanish': `SPA ${authorCode}`
  };

  let callNumber = rules[location] || `${authorCode}`;

  return callNumber;
  };

  // NEW: Update Firebase barcode immediately when used
  const incrementBarcodeInFirebase = async (usedBarcode) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        lastBarcodeUsed: usedBarcode,
        lastUpdated: new Date()
      });
      console.log(`✅ Barcode ${usedBarcode} saved to Firebase`);
    } catch (error) {
      console.error('Error updating barcode in Firebase:', error);
    }
  };

  // Remove book from batch with barcode reclamation
  const removeBookFromBatch = async (index) => {
    const bookToRemove = batch[index];
    if (!bookToRemove) return;
    
    // Check if this book has the most recent barcode (can reclaim it)
    const bookBarcode = parseInt(bookToRemove.barcode);
    const canReclaimBarcode = !isNaN(bookBarcode) && bookBarcode === currentBarcode - 1;
    
    if (canReclaimBarcode) {
      // Reclaim the barcode - decrement counter
      const newBarcode = currentBarcode - 1;
      setCurrentBarcode(newBarcode);
      
      // Update Firebase too
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { lastBarcodeUsed: newBarcode - 1 });
        console.log(`♻️ Reclaimed barcode ${bookBarcode}, next barcode is now ${newBarcode}`);
      } catch (error) {
        console.error('Error reclaiming barcode:', error);
      }
    } else {
      console.log(`🗑️ Removed book (barcode ${bookToRemove.barcode} not reclaimed - would create gap)`);
    }
    
    // Remove from batch
    setBatch(batch.filter((_, i) => i !== index));
  };

  // Re-catalog a book by searching again
  const handleRecatalogSearch = async (index) => {
    const book = batch[index];
    if (!book) return;

    setRecatalogingBookIndex(null);
    setLoading(true);

    try {
      // Determine search method based on book data
      const hasIsbn = book.isbn && book.isbn !== 'No ISBN';
      const searchData = hasIsbn
        ? { isbn: book.isbn.replace(/[-\s]/g, '') }
        : {
            title: book.title,
            authorFirst: book.author.split(' ')[0] || '',
            authorLast: book.author.split(' ').slice(1).join(' ') || book.author
          };

      console.log('🔄 Re-cataloging book:', book.title);

      // Run the same multi-library search
      const libraryResults = await multiLibrarySearch.searchAllLibraries(searchData);

      if (libraryResults.merged) {
        // Preserve certain fields from the original book
        const updatedBook = {
          ...libraryResults.merged,
          barcode: book.barcode, // Keep same barcode
          location: book.location, // Keep current location
          easyReaderSubcategory: book.easyReaderSubcategory,
          callNumber: book.callNumber, // Keep current call number
          isOversized: book.isOversized,
          searchMethod: hasIsbn ? 'isbn' : 'title',
          librarySources: libraryResults.sources,
          createdAt: book.createdAt,
          catalogedDate: book.catalogedDate
        };

        // Replace in batch
        const updatedBatch = [...batch];
        updatedBatch[index] = updatedBook;
        setBatch(updatedBatch);

        console.log('✅ Book re-cataloged successfully');
      } else {
        alert('No results found. Try manual entry instead.');
      }
    } catch (error) {
      console.error('Error re-cataloging book:', error);
      alert('Failed to re-catalog book. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open manual entry for re-cataloging
  const handleRecatalogManual = (index) => {
    const book = batch[index];
    if (!book) return;

    setRecatalogingBookIndex(null);

    // Pre-fill manual entry with current book data
    setManualBookData({
      title: book.title || '',
      subtitle: book.subtitle || '',
      author: book.author || '',
      publisher: book.publisher || '',
      publishDate: book.publishDate || '',
      pages: book.pages || '',
      isbn13: book.isbn || '',
      ficOrNonfic: book.ficOrNonfic || 'Fiction',
      deweyDecimal: book.deweyDecimal || '',
      description: book.description || '',
      location: book.location || 'Fiction',
      easyReaderSubcategory: book.easyReaderSubcategory || 'ER1',
      subjects: book.subjects || [''],
      genres: book.genres || [''],
      targetAudience: book.targetAudience || '',
      ageRange: book.ageRange || '',
      lexileLevel: book.lexileLevel || '',
      physicalFormat: book.physicalFormat || 'Print',
      generalNotes: book.generalNotes?.[0] || '',
      series: book.series || '',
      seriesNumber: book.seriesNumber || '',
      subjectPerson: book.subjectPerson || '',
      _recatalogIndex: index, // Store which book we're recataloging
      _originalBarcode: book.barcode // Store original barcode
    });

    setManualEntryMode(true);
  };

  // On-demand AI enrichment for a specific book in the batch
  const handleAskAI = async (index) => {
    const book = batch[index];
    if (!book) return;

    setEnrichingIndex(index);

    try {
      const isbn = (book.isbn13 || book.isbn || '').replace(/[-\s]/g, '');
      let enrichmentData = null;

      // Check cache first
      if (isbn && isbn !== 'NoISBN') {
        try {
          const cacheRef = doc(db, 'users', currentUser.uid, 'aiCache', isbn);
          const cacheDoc = await getDoc(cacheRef);
          if (cacheDoc.exists() && cacheDoc.data().enrichment) {
            enrichmentData = cacheDoc.data().enrichment;
            console.log('📦 Using cached AI enrichment for', isbn);
          }
        } catch (e) { /* ignore cache miss */ }
      }

      // If no cache, call AI
      if (!enrichmentData) {
        const searchData = isbn && isbn !== 'NoISBN'
          ? { isbn }
          : { title: book.title, authorFirst: '', authorLast: book.author };

        enrichmentData = await searchBookEnrichment(searchData, book, book.location);

        // Cache the results
        if (isbn && isbn !== 'NoISBN') {
          try {
            const cacheRef = doc(db, 'users', currentUser.uid, 'aiCache', isbn);
            await setDoc(cacheRef, {
              enrichment: enrichmentData,
              cachedAt: new Date(),
              title: book.title
            }, { merge: true });
          } catch (cacheError) {
            console.warn('Cache write failed (non-fatal):', cacheError);
          }
        }
      }

      // Merge: only fill empty fields, never override existing data
      const updatedBook = { ...book };

      if (!updatedBook.deweyDecimal && enrichmentData.deweyDecimal) {
        updatedBook.deweyDecimal = enrichmentData.deweyDecimal;
      }
      if (!updatedBook.subjectPerson && enrichmentData.subjectPerson) {
        updatedBook.subjectPerson = enrichmentData.subjectPerson;
      }
      if (!updatedBook.series && enrichmentData.series) {
        updatedBook.series = enrichmentData.series;
      }
      if (!updatedBook.seriesNumber && enrichmentData.seriesNumber) {
        updatedBook.seriesNumber = enrichmentData.seriesNumber;
      }
      if ((!updatedBook.forFansOf || updatedBook.forFansOf.length === 0) && enrichmentData.forFansOf?.length > 0) {
        updatedBook.forFansOf = enrichmentData.forFansOf;

        // Add "For fans of" to general notes for fiction (not non-fiction or quick)
        const isFiction = updatedBook.ficOrNonfic === 'Fiction';
        const isNotQuick = !updatedBook.location?.includes('Quick');
        if (isFiction && isNotQuick) {
          const fansNote = `For fans of: ${enrichmentData.forFansOf.join(', ')}`;
          if (!updatedBook.generalNotes?.includes(fansNote)) {
            updatedBook.generalNotes = [...(updatedBook.generalNotes || []), fansNote];
          }
        }
      }
      if (!updatedBook.description && enrichmentData.description) {
        updatedBook.description = enrichmentData.description;
      }
      if (enrichmentData.studyPrograms?.length > 0) {
        updatedBook.studyPrograms = [...new Set([...(updatedBook.studyPrograms || []), ...enrichmentData.studyPrograms])];
      }

      // Store suggested location for display (don't auto-change)
      if (enrichmentData.suggestedLocation && enrichmentData.suggestedLocation !== updatedBook.location) {
        updatedBook.aiSuggestedLocation = enrichmentData.suggestedLocation;
      }

      // Regenerate call number if we got new data that affects it
      if (enrichmentData.subjectPerson || enrichmentData.deweyDecimal) {
        updatedBook.callNumber = generateCallNumber(
          updatedBook.location,
          updatedBook.deweyDecimal,
          updatedBook.author,
          updatedBook.isOversized,
          updatedBook.easyReaderSubcategory,
          updatedBook.subjectPerson,
          updatedBook.isGN2
        );
      }

      updatedBook.aiEnriched = true;

      const updatedBatch = [...batch];
      updatedBatch[index] = updatedBook;
      setBatch(updatedBatch);

    } catch (error) {
      console.error('AI enrichment failed:', error);
      alert('AI enrichment failed: ' + error.message);
    } finally {
      setEnrichingIndex(null);
    }
  };

  const writeFlaggedBookToFirebase = async (bookData, duplicateInfo = null) => {
    try {
      const flaggedRef = collection(db, 'users', currentUser.uid, 'flaggedBooks');
      const flaggedBook = {
        ...bookData,
        flaggedAt: new Date(),
        status: 'flagged_for_review'
      };

      if (duplicateInfo) {
        flaggedBook.duplicateInfo = duplicateInfo;
        flaggedBook.isDuplicateFlag = true;
      }

      await addDoc(flaggedRef, flaggedBook);
      
      // NO barcode update - flagged books use temporary IDs until approved
      
      setFlaggedCount(prev => prev + 1);
    } catch (error) {
      console.error('Error writing flagged book:', error);
      throw error;
    }
  };

  const getPhysicalDescription = (format, pages) => {
    switch(format) {
      case 'Audio':
        return '1 audiobook';
      case 'Digital':
        return '1 electronic resource';
      case 'DVD':
        return '1 videodisc';
      case 'Kit':
        return '1 kit (various pieces)';
      case 'Print':
      default:
        return pages ? `${pages} pages` : 'Print';
    }
  };

  // ==================== BARCODE SCANNER ====================
  const handleBarcodeScan = useCallback((isbn) => {
    pendingScanIsbn.current = isbn;
    setCurrentIsbn(isbn);
    setSearchMode('isbn');
  }, []);

  // Auto-trigger search when ISBN is set from scanner
  useEffect(() => {
    if (pendingScanIsbn.current && currentIsbn === pendingScanIsbn.current) {
      pendingScanIsbn.current = null;
      handleAddBook();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIsbn]);

  // ==================== MAIN HANDLERS ====================
  const handleAddBook = async () => {
    const fullAuthor = `${authorFirst} ${authorLast}`.trim();
    
    if (searchMode === 'isbn' && !currentIsbn.trim()) return;
    if (searchMode === 'title' && !searchTitle.trim() && !fullAuthor) return;
    
    setLoading(true);
    setSearchStats(null);
    
    try {
      const searchData = searchMode === 'isbn' 
        ? { isbn: currentIsbn.replace(/[-\s]/g, '') }
        : { title: searchTitle.trim(), authorFirst: authorFirst.trim(), authorLast: authorLast.trim() };
      
      let bookData = {};
      let libraryResults = null;
      
      // Check if we have Amazon details to parse
      if (amazonDetails.trim()) {
        console.log('📦 Parsing Amazon details...');
        const amazonParsed = parseAmazonDetails(amazonDetails);
        bookData = { ...amazonParsed };
        console.log('📦 Amazon data extracted:', Object.keys(amazonParsed));
      }
      
      // Phase 1: Multi-Library Search (skip if we have Amazon details)
      if (!amazonDetails.trim()) {
        console.log('🔍 Starting search process...');
        console.log('📊 Using all library sources: LoC, Open Library, Google Books');
        
        try {
          libraryResults = await multiLibrarySearch.searchAllLibraries(searchData);
          
          setSearchStats({
            librariesSearched: libraryResults.sources ? Object.keys(libraryResults.sources).length : 0,
            successfulLibraries: libraryResults.successfulLibraries || [],
            errors: libraryResults.errors || []
          });
          
          if (libraryResults.merged) {
            bookData = {
              ...libraryResults.merged,
              searchMethod: searchMode,
              librarySources: libraryResults.sources
            };
          }
          
          const successCount = Object.keys(libraryResults.sources || {}).length;
          console.log(`📚 Library search complete: ${successCount} sources returned data`);
          
          if (libraryResults.successfulLibraries?.length > 0) {
            console.log(`✅ Data received from: ${libraryResults.successfulLibraries.join(', ')}`);
          }
          
          if (libraryResults.errors?.length > 0) {
            libraryResults.errors.forEach(err => {
              console.warn(`⚠️ ${err.library}: ${err.error}`);
            });
          }
        } catch (libraryError) {
          console.error('Multi-library search failed:', libraryError);
        }
      }
      
      // Phase 2: Content Check (automatic, lightweight - enrichment is on-demand via Ask AI button)
      if (contentFilterSettings.enableContentFiltering && bookData.title) {
        const cacheIsbn = (bookData.isbn13 || bookData.isbn10 || (searchMode === 'isbn' ? currentIsbn : '')).replace(/[-\s]/g, '');
        let cachedContentCheck = false;

        // Check cache first
        if (cacheIsbn) {
          try {
            const cacheRef = doc(db, 'users', currentUser.uid, 'aiCache', cacheIsbn);
            const cacheDoc = await getDoc(cacheRef);
            if (cacheDoc.exists() && cacheDoc.data().contentCheck) {
              const cached = cacheDoc.data().contentCheck;
              // Re-run if cache is missing subjects/genres (old format)
              if (!cached.subjects || cached.subjects.length === 0) {
                console.log('♻️ Stale cache (missing subjects/genres), re-running content check for', cacheIsbn);
              } else {
                bookData.contentFlags = cached.contentFlags || {};
                bookData.lgbtqDetails = cached.lgbtqDetails || '';
                bookData.lgbtqSeverity = cached.lgbtqSeverity || 'NONE';
                if (cached.subjects?.length > 0) {
                  bookData.subjects = [...new Set([...(bookData.subjects || []), ...cached.subjects])];
                }
                if (cached.genres?.length > 0) {
                  bookData.genres = [...new Set([...(bookData.genres || []), ...cached.genres])];
                }
                if (cached.forFansOf?.length > 0) {
                  bookData.forFansOf = [...new Set([...(bookData.forFansOf || []), ...cached.forFansOf])];
                }
                cachedContentCheck = true;
              }
              console.log('📦 Using cached content check for', cacheIsbn);
            }
          } catch (e) { /* ignore cache miss */ }
        }

        if (!cachedContentCheck) {
          console.log('🛡️ Running content check...');
          try {
            const contentCheckData = await searchBookContentCheck(searchData, bookData);
            bookData.contentFlags = contentCheckData.contentFlags || {};
            bookData.lgbtqDetails = contentCheckData.lgbtqDetails || '';
            bookData.lgbtqSeverity = contentCheckData.lgbtqSeverity || 'NONE';

            // Merge AI subjects/genres/forFansOf with library data
            if (contentCheckData.subjects?.length > 0) {
              bookData.subjects = [...new Set([...(bookData.subjects || []), ...contentCheckData.subjects])];
            }
            if (contentCheckData.genres?.length > 0) {
              bookData.genres = [...new Set([...(bookData.genres || []), ...contentCheckData.genres])];
            }
            if (contentCheckData.forFansOf?.length > 0) {
              bookData.forFansOf = [...new Set([...(bookData.forFansOf || []), ...contentCheckData.forFansOf])];
            }

            // Cache the content check results
            if (cacheIsbn) {
              try {
                const cacheRef = doc(db, 'users', currentUser.uid, 'aiCache', cacheIsbn);
                await setDoc(cacheRef, {
                  contentCheck: contentCheckData,
                  cachedAt: new Date(),
                  title: bookData.title || ''
                }, { merge: true });
              } catch (cacheError) {
                console.warn('Cache write failed (non-fatal):', cacheError);
              }
            }
          } catch (contentError) {
            console.error('Content check failed (non-fatal):', contentError);
          }
        }
      }

      // If no library data found and no title, show not found
      if (!bookData.title) {
        setNotFound(true);
        setManualBookData({
          title: searchMode === 'title' ? searchTitle : '',
          author: searchMode === 'title' ? `${authorLast}, ${authorFirst}`.trim() : '',
          publisher: '',
          publishDate: '',
          pages: '',
          isbn13: searchMode === 'isbn' ? currentIsbn : '',
          description: '',
          ficOrNonfic: 'Fiction'
        });
        setLoading(false);
        return;
      }

      // Check if AI returned a valid book (not hallucinated)
      // If we had no library sources and the AI response has no ISBN match, likely hallucinated
      const noLibrarySources = !libraryResults || Object.keys(libraryResults.sources || {}).length === 0;
      const isbnMismatch = searchMode === 'isbn' && bookData.isbn13 &&
        !bookData.isbn13.includes(currentIsbn.replace(/[-\s]/g, '').slice(-10));

      if (noLibrarySources && isbnMismatch && !amazonDetails.trim()) {
        console.log('⚠️ Possible AI hallucination detected - ISBN mismatch with no library confirmation');
        setNotFound(true);
        setManualBookData({
          title: '',
          author: '',
          publisher: '',
          publishDate: '',
          pages: '',
          isbn13: currentIsbn,
          description: '',
          ficOrNonfic: 'Fiction'
        });
        setLoading(false);
        return;
      }
      
      // Phase 3: Price handling
      if (manualPrice && parseFloat(manualPrice) > 0) {
        bookData.listPrice = parseFloat(manualPrice);
        bookData.priceSource = 'Manual entry';
      } else if (selectedPriceTier) {
        bookData.listPrice = selectedPriceTier;
        bookData.priceSource = `Price tier ($${selectedPriceTier})`;
      } else {
        bookData.listPrice = 16.99;
        bookData.priceSource = 'Default estimate';
      }
      
      // Phase 4: Format author for cataloging
      if (bookData.author && !bookData.author.includes(',')) {
        const nameParts = bookData.author.split(' ');
        if (nameParts.length > 1) {
          const lastName = nameParts.pop();
          const firstName = nameParts.join(' ');
          bookData.author = `${lastName}, ${firstName}`;
          bookData.rawAuthor = `${firstName} ${lastName}`;
        }
      }
      
      // Phase 5: Check for duplicates
      let duplicateCheck = { isDuplicate: false, duplicateInfo: null };
      if (contentFilterSettings.enableDuplicateChecking && checkDuplicatesForThisBook) {
        duplicateCheck = await checkOPACDuplicates(bookData, searchMode === 'isbn' ? currentIsbn : bookData.isbn13);
      }
      
      // Phase 6: Determine location
      let location = applyLocationToAll 
        ? globalLocation 
        : determineLocation(
            bookData.targetAudience, 
            bookData.ficOrNonfic, 
            bookData.genres,
            bookData.ageRange,
            bookData.readingLevel
          );
      
      // For oversized books: keep the original location for call number formatting,
      // but store both so MARC export can use "Oversized" as the shelving location
      const shelvingLocation = isOversized ? 'Oversized' : location;
      
      const physicalFormat = applyPhysicalFormatToAll ? globalPhysicalFormat : 'Print';
      const physicalDesc = getPhysicalDescription(physicalFormat, bookData.pages);
      
      // NEW: Clear forFansOf for non-fiction and quick readers
      const isFiction = bookData.ficOrNonfic === 'Fiction';
      const isNotQuick = !location.includes('Quick');
      if (!isFiction || !isNotQuick) {
        bookData.forFansOf = []; // Clear the field entirely for non-fiction/quick
      }
      
      // Phase 7: Check content flags
      const flags = checkContentFlags(bookData, duplicateCheck);
      const needsReview = flags.length > 0;
      
      // Build general notes
      const generalNotes = [];
      if (customNote) generalNotes.push(customNote);
      
      // Add "For fans of" ONLY for fiction (not non-fiction or quick readers)
if (bookData.forFansOf && bookData.forFansOf.length > 0) {
  const isFiction = bookData.ficOrNonfic === 'Fiction';
  const isNotQuick = !location.includes('Quick');
  
  if (isFiction && isNotQuick) {
    generalNotes.push(`For fans of: ${bookData.forFansOf.join(', ')}`);
  }
}
      
      // Handle Lux Libris award with year
      const processedAwards = selectedAwards.map(award => {
        if (award === 'Lux Libris Award nominee' && luxLibrisYear) {
          return `Lux Libris Award nominee (${luxLibrisYear})`;
        }
        return award;
      });

      // Subjects — no longer mixing in acquisition notes
      const allSubjects = [
        ...(bookData.subjects || []),
        ...processedAwards
      ];

      // Build copy notes array from selections
      const copyNotesArr = [];
      if (copyNoteOptions.libraryPurchase) copyNotesArr.push('Library Purchase');
      if (copyNoteOptions.donation) {
        copyNotesArr.push(copyNoteOptions.donorName ? `Donation: ${copyNoteOptions.donorName}` : 'Donation');
      }
      if (copyNoteOptions.damage) copyNotesArr.push('Damaged');
      if (copyNoteOptions.tla) {
        copyNotesArr.push(copyNoteOptions.tlaYear ? `TLA (${copyNoteOptions.tlaYear})` : 'TLA');
      }
      if (copyNoteOptions.bindingType) copyNotesArr.push(copyNoteOptions.bindingType);
      
      // Build final book record
      // Get default audience values based on location (for when data is missing)
      const locationDefaults = getDefaultAudienceByLocation(location);

      // Set Easy Reader subcategory - use global override if location override is active
      const easyReaderSubcategory = location === 'Easy Reader'
        ? (applyLocationToAll ? globalEasyReaderSub : 'ER1')
        : null;
      const bookIsGN2 = location === 'Graphic Novels' && applyLocationToAll ? globalGN2 : false;

      const newBook = {
        isbn: searchMode === 'isbn' ? currentIsbn : (bookData.isbn13 || bookData.isbn10 || 'No ISBN'),
        barcode: needsReview ? `PENDING-${Date.now()}` : currentBarcode.toString(), // TEMP barcode for flagged books
        title: toTitleCase(bookData.title) || 'Unknown Title',
        subtitle: toTitleCase(bookData.subtitle) || '',
        author: bookData.author || 'Unknown Author',
        rawAuthor: bookData.rawAuthor || bookData.author,
        coAuthors: bookData.coAuthors || [],
        publisher: bookData.publisher || 'Unknown Publisher',
        publishDate: bookData.publishDate || new Date().getFullYear().toString(),
        location: shelvingLocation,
        originalLocation: location,  // Store original for reference
        isOversized,
        easyReaderSubcategory,
        isGN2: bookIsGN2,
        callNumber: generateCallNumber(location, bookData.deweyDecimal, bookData.author, isOversized, easyReaderSubcategory, bookData.subjectPerson, bookIsGN2),
        subjects: allSubjects,
        indexTerms: bookData.indexTerms || [],
        targetAudience: bookData.targetAudience || locationDefaults.targetAudience,
        ageRange: bookData.ageRange || locationDefaults.ageRange,
        recommendedAge: bookData.recommendedAge || locationDefaults.recommendedAge,
        readingLevel: bookData.readingLevel || '',
        lexileLevel: bookData.lexileLevel || locationDefaults.lexileLevel,
        studyPrograms: bookData.studyPrograms || [],
        genres: bookData.genres || [],
        ficOrNonfic: bookData.ficOrNonfic || 'Unknown',
        deweyDecimal: bookData.deweyDecimal || '',
        listPrice: bookData.listPrice || 0,
        libraryPrice: bookData.listPrice ? (bookData.listPrice + 1) : 1,
        priceNote: bookData.priceNote || '',
        priceSource: bookData.priceSource || '',
        description: bookData.description || '',
        generalNotes: generalNotes,
        awards: processedAwards,
        forFansOf: bookData.forFansOf || [],
        searchMethod: searchMode,
        needsReview,
        reviewFlags: flags,
        reviewStatus: needsReview ? 'flagged_for_review' : 'pending',
        status: 'pending',
        createdAt: new Date(),
        catalogedDate: new Date().toISOString().split('T')[0],
        contentFlags: bookData.contentFlags || {},
        lgbtqDetails: bookData.lgbtqDetails || '',
        lgbtqSeverity: bookData.lgbtqSeverity || 'NONE',
        physicalDesc,
        physicalFormat,
        copyNotes: copyNotesArr,
        coverUrl: bookData.coverUrl || '',
        language: bookData.language || 'eng',
        isbn13: bookData.isbn13 || '',
        isbn10: bookData.isbn10 || '',
        series: bookData.series || '',
        seriesNumber: bookData.seriesNumber || '',
        subjectPerson: bookData.subjectPerson || '',
        edition: bookData.edition || '',  // NEW: Edition statement
        lccn: bookData.lccn || '',        // NEW: Library of Congress Control Number
        notes: '',
        dataConfidence: bookData.confidence || 0,
        dataSources: bookData.sources || []
      };
      
      // Route to appropriate destination
      if (needsReview) {
        await writeFlaggedBookToFirebase(newBook, duplicateCheck.isDuplicate ? duplicateCheck.duplicateInfo : null);
        // DON'T increment barcode - flagged books use temporary IDs
        console.log(`📌 Book flagged with temporary ID: ${newBook.barcode}`);
      } else {
        // Add directly to batch with real barcode
        setBatch([...batch, newBook]);
        // Update Firebase immediately with used barcode
        await incrementBarcodeInFirebase(currentBarcode);
        setCurrentBarcode(currentBarcode + 1);
        console.log(`✅ Book added with barcode: ${newBook.barcode}`);
      }
      
      // Clear inputs — reset settings too unless Batch Mode is on
      setCurrentIsbn('');
      setSearchTitle('');
      setAuthorFirst('');
      setAuthorLast('');
      setCheckDuplicatesForThisBook(false);
      setSelectedAwards([]);
      setCustomNote('');
      setLuxLibrisYear('');
      setManualPrice('');
      setAmazonDetails('');
      if (!batchMode) {
        setApplyLocationToAll(false);
        setGlobalGN2(false);
        setGlobalEasyReaderSub('ER1');
        setIsOversized(false);
        setApplyPhysicalFormatToAll(false);
        setGlobalPhysicalFormat('Print');
        setSelectedPriceTier(null);
        setShowCopyNotes(false);
        setCopyNoteOptions({ libraryPurchase: false, donation: false, donorName: '', damage: false, bindingType: '', tla: false, tlaYear: '' });
      } else {
        setIsOversized(false);
      }
      
      // Final summary
      if (amazonDetails.trim()) {
        console.log('✅ Book successfully added using Amazon details!');
      } else if (searchStats) {
        console.log('✅ Book successfully added!');
        console.log(`📊 Final stats: ${searchStats.successfulLibraries.length} libraries provided data`);
        if (bookData.listPrice) {
          console.log(`💰 Price: $${bookData.listPrice} (${bookData.priceSource})`);
        }
      }
      
    } catch (error) {
      console.error('❌ Error adding book:', error);
      alert(`Error adding book: ${error.message}\n\nPlease check the ISBN/title and try again.`);
    }
    setLoading(false);
  };

  const processingRef = useRef(false);

  const processBatch = async () => {
    if (batch.length === 0) return;
    if (processingRef.current) return; // Prevent double-processing
    processingRef.current = true;

    setLoading(true);
    try {
      // Deduplicate batch by barcode before writing (keep first occurrence)
      const seenBarcodes = new Set();
      const deduped = batch.filter(book => {
        if (seenBarcodes.has(book.barcode)) {
          console.log(`⚠️ Removed duplicate barcode ${book.barcode} from batch before processing`);
          return false;
        }
        seenBarcodes.add(book.barcode);
        return true;
      });

      const batchPromises = deduped.map(book =>
        addDoc(collection(db, 'users', currentUser.uid, 'books'), book)
      );

      await Promise.all(batchPromises);

      // Find the highest barcode in the batch to ensure consistency
      const highestBarcode = Math.max(...deduped.map(book => parseInt(book.barcode)));

      await updateDoc(doc(db, 'users', currentUser.uid), {
        lastBarcodeUsed: highestBarcode,
        lastUpdated: new Date()
      });

      console.log(`✅ Batch processed: ${deduped.length} books, last barcode: ${highestBarcode}`);

      setBatch([]);
      clearBatchStorage();
      navigate('/asgard');

    } catch (error) {
      console.error('Error processing batch:', error);
      alert('Error processing batch: ' + error.message);
      setLoading(false);
      processingRef.current = false;
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  // Handle manual entry submission
  const handleManualEntry = async () => {
    if (!manualBookData.title.trim()) {
      alert('Please enter at least a title');
      return;
    }

    setLoading(true);

    try {
      // Format author if needed
      let formattedAuthor = manualBookData.author;
      if (formattedAuthor && !formattedAuthor.includes(',')) {
        const nameParts = formattedAuthor.split(' ');
        if (nameParts.length > 1) {
          const lastName = nameParts.pop();
          const firstName = nameParts.join(' ');
          formattedAuthor = `${lastName}, ${firstName}`;
        }
      }

      // Use location from manualBookData or apply global override
      const location = applyLocationToAll ? globalLocation : (manualBookData.location || 'Fiction');
      const physicalFormat = applyPhysicalFormatToAll ? globalPhysicalFormat : (manualBookData.physicalFormat || 'Print');

      // Generate call number using the new function with Easy Reader support
      const effectiveEasyReaderSub = location === 'Easy Reader'
        ? (applyLocationToAll ? globalEasyReaderSub : manualBookData.easyReaderSubcategory)
        : null;
      const effectiveGN2 = location === 'Graphic Novels'
        ? (applyLocationToAll ? globalGN2 : manualBookData.isGN2)
        : false;
      const callNumber = generateCallNumber(
        location,
        manualBookData.deweyDecimal,
        formattedAuthor,
        isOversized,
        effectiveEasyReaderSub,
        manualBookData.subjectPerson,
        effectiveGN2
      );

      // Clean and filter subjects/genres
      const cleanedSubjects = manualBookData.subjects?.map(s => s.trim()).filter(s => s) || [];
      const cleanedGenres = manualBookData.genres?.map(g => g.trim()).filter(g => g) || [];
      const cleanedNotes = manualBookData.generalNotes ? [manualBookData.generalNotes.trim()].filter(n => n) : [];
      if (customNote) cleanedNotes.push(customNote);

      const newBook = {
        title: toTitleCase(manualBookData.title.trim()),
        subtitle: toTitleCase(manualBookData.subtitle || ''),
        author: formattedAuthor || 'Unknown',
        rawAuthor: manualBookData.author,
        barcode: currentBarcode.toString(),
        callNumber,
        location: isOversized ? 'Oversized' : location,
        originalLocation: location,
        shelvingLocation: isOversized ? 'Oversized' : location,
        easyReaderSubcategory: location === 'Easy Reader' ? manualBookData.easyReaderSubcategory : null,
        status: 'cataloged',
        publisher: manualBookData.publisher || 'Unknown',
        publishDate: manualBookData.publishDate || new Date().getFullYear().toString(),
        subjects: cleanedSubjects,
        genres: cleanedGenres,
        indexTerms: [],
        pages: manualBookData.pages || '',
        listPrice: parseFloat(manualPrice) || selectedPriceTier || 16.99,
        libraryPrice: (parseFloat(manualPrice) || selectedPriceTier || 16.99) + 1,
        priceSource: manualPrice ? 'Manual entry' : selectedPriceTier ? `Price tier ($${selectedPriceTier})` : 'Default estimate',
        awards: selectedAwards,
        generalNotes: cleanedNotes,
        targetAudience: manualBookData.targetAudience || '',
        ageRange: manualBookData.ageRange || '',
        lexileLevel: manualBookData.lexileLevel || '',
        gradeLevel: '',
        description: manualBookData.description || '',
        series: manualBookData.series || '',
        seriesNumber: manualBookData.seriesNumber || '',
        subjectPerson: manualBookData.subjectPerson || '',
        catalogedDate: new Date().toISOString().split('T')[0],
        createdAt: new Date(),
        contentFlags: {},
        physicalDesc: manualBookData.pages ? `${manualBookData.pages} pages` : 'Print',
        physicalFormat,
        copyNotes: (() => {
          const notes = [];
          if (copyNoteOptions.libraryPurchase) notes.push('Library Purchase');
          if (copyNoteOptions.donation) notes.push(copyNoteOptions.donorName ? `Donation: ${copyNoteOptions.donorName}` : 'Donation');
          if (copyNoteOptions.damage) notes.push('Damaged');
          if (copyNoteOptions.tla) notes.push(copyNoteOptions.tlaYear ? `TLA (${copyNoteOptions.tlaYear})` : 'TLA');
          if (copyNoteOptions.bindingType) notes.push(copyNoteOptions.bindingType);
          return notes;
        })(),
        ficOrNonfic: manualBookData.ficOrNonfic,
        deweyDecimal: manualBookData.deweyDecimal || '',
        isbn: manualBookData.isbn13 || 'No ISBN',
        isbn13: manualBookData.isbn13 || '',
        isbn10: '',
        dataSource: 'Manual entry',
        dataSources: ['Manual entry']
      };

      // Check if we're recataloging an existing book
      if (manualBookData._recatalogIndex !== undefined) {
        // Replace existing book in batch
        const updatedBatch = [...batch];
        updatedBatch[manualBookData._recatalogIndex] = {
          ...newBook,
          barcode: manualBookData._originalBarcode || batch[manualBookData._recatalogIndex]?.barcode // Use stored barcode
        };
        setBatch(updatedBatch);
        console.log(`✅ Book re-cataloged at index ${manualBookData._recatalogIndex}`);
      } else {
        // Add new book to batch
        setBatch([...batch, newBook]);
        await incrementBarcodeInFirebase(currentBarcode);
        setCurrentBarcode(currentBarcode + 1);
        console.log(`✅ Manual entry added with barcode: ${newBook.barcode}`);
      }

      // Reset states
      setNotFound(false);
      setManualEntryMode(false);
      setManualBookData({
        title: '',
        subtitle: '',
        author: '',
        publisher: '',
        publishDate: '',
        pages: '',
        isbn13: '',
        description: '',
        ficOrNonfic: 'Fiction',
        deweyDecimal: '',
        location: 'Fiction',
        easyReaderSubcategory: 'ER1',
        subjects: [''],
        genres: [''],
        targetAudience: '',
        ageRange: '',
        lexileLevel: '',
        physicalFormat: 'Print',
        generalNotes: '',
        series: ''
      });
      setCurrentIsbn('');
      setSearchTitle('');
      setAuthorFirst('');
      setAuthorLast('');
      setCustomNote('');
      setManualPrice('');
      setSelectedAwards([]);
      if (!batchMode) {
        setApplyLocationToAll(false);
        setGlobalGN2(false);
        setGlobalEasyReaderSub('ER1');
        setIsOversized(false);
        setApplyPhysicalFormatToAll(false);
        setGlobalPhysicalFormat('Print');
        setSelectedPriceTier(null);
        setShowCopyNotes(false);
        setCopyNoteOptions({ libraryPurchase: false, donation: false, donorName: '', damage: false, bindingType: '', tla: false, tlaYear: '' });
      } else {
        setIsOversized(false);
      }

    } catch (error) {
      console.error('Error adding manual entry:', error);
      alert('Error adding book: ' + error.message);
    }

    setLoading(false);
  };

  // Cancel not found / manual entry mode
  const cancelNotFound = () => {
    setNotFound(false);
    setManualEntryMode(false);
    setManualBookData({
      title: '',
      author: '',
      publisher: '',
      publishDate: '',
      pages: '',
      isbn13: '',
      description: '',
      ficOrNonfic: 'Fiction'
    });
  };

  // ==================== ADD COPIES MODE ====================
  const handleCallNumberChange = (value) => {
    setCopyCallNumber(value);

    // Auto-fill title if call number matches
    const match = allCallNumbers.find(item => item.callNumber === value);
    if (match) {
      setCopyTitle(match.title);
    }
  };

  const handleAddCopies = () => {
    if (!copyCallNumber.trim()) {
      alert('Please enter a call number');
      return;
    }

    if (!copyTitle.trim()) {
      alert('Please enter a title');
      return;
    }

    const newCopies = [];
    for (let i = 0; i < copyCount; i++) {
      newCopies.push({
        callNumber: copyCallNumber.trim(),
        title: copyTitle.trim(),
        barcode: (currentBarcode + i).toString(),
        copyOnly: true, // Flag to skip MARC generation
        author: 'Copy',
        dataSource: 'Add Copies',
        dataSources: ['Add Copies']
      });
    }

    setBatch([...batch, ...newCopies]);
    setCurrentBarcode(currentBarcode + copyCount);

    // Reset form
    setCopyCallNumber('');
    setCopyTitle('');
    setCopyCount(1);
  };

  // Fetch all call numbers for autocomplete in Add Copies mode
  const [allCallNumbers, setAllCallNumbers] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    const fetchCallNumbers = async () => {
      try {
        const booksRef = collection(db, 'users', currentUser.uid, 'books');
        const booksSnapshot = await getDocs(booksRef);

        const callNumbersMap = new Map();
        booksSnapshot.forEach(doc => {
          const book = doc.data();
          if (book.callNumber && book.title) {
            callNumbersMap.set(book.callNumber, book.title);
          }
        });

        setAllCallNumbers(Array.from(callNumbersMap.entries()).map(([cn, title]) => ({ callNumber: cn, title })));
      } catch (error) {
        console.error('Error fetching call numbers:', error);
      }
    };

    fetchCallNumbers();
  }, [currentUser]);

  // ==================== EDIT MODAL COMPONENT ====================
  const BookEditModal = ({ book, index }) => {
    const [editedBook, setEditedBook] = useState({ ...book });
    
    if (editingBookIndex !== index) return null;
    
    const updateBook = (field, value) => {
      setEditedBook(prev => {
        const updated = { ...prev, [field]: value };

        // Auto-regenerate call number when location, dewey, subcategory, subjectPerson, or GN2 changes
        if (field === 'location') {
          // If switching to Easy Reader, set default subcategory
          if (value === 'Easy Reader' && !updated.easyReaderSubcategory) {
            updated.easyReaderSubcategory = 'ER1';
          }
          // Reset GN2 if switching away from Graphic Novels
          if (value !== 'Graphic Novels') {
            updated.isGN2 = false;
          }
          updated.callNumber = generateCallNumber(value, updated.deweyDecimal, updated.author, updated.isOversized, updated.easyReaderSubcategory, updated.subjectPerson, updated.isGN2);
        } else if (field === 'isGN2') {
          updated.callNumber = generateCallNumber(updated.location, updated.deweyDecimal, updated.author, updated.isOversized, updated.easyReaderSubcategory, updated.subjectPerson, value);
        } else if (field === 'easyReaderSubcategory') {
          updated.callNumber = generateCallNumber(updated.location, updated.deweyDecimal, updated.author, updated.isOversized, value, updated.subjectPerson, updated.isGN2);
        } else if (field === 'deweyDecimal' && (updated.location === 'Non-Fiction' || updated.location === 'MS Non-Fiction' || updated.location === 'MS Quick')) {
          updated.callNumber = generateCallNumber(updated.location, value, updated.author, updated.isOversized, updated.easyReaderSubcategory, updated.subjectPerson, updated.isGN2);
        } else if (field === 'subjectPerson' && updated.location === 'MS Quick') {
          updated.callNumber = generateCallNumber(updated.location, updated.deweyDecimal, updated.author, updated.isOversized, updated.easyReaderSubcategory, value, updated.isGN2);
        }

        return updated;
      });
    };

    const handleBarcodeChange = (newBarcode) => {
      const oldBarcode = editedBook.barcode;

      // If clearing/deleting the barcode
      if (!newBarcode || newBarcode.trim() === '') {
        const confirmed = window.confirm(
          `Are you sure you want to remove barcode ${oldBarcode}?\n\n` +
          `If this is the most recently assigned barcode, it will be returned to the pool and reused for the next book.`
        );

        if (confirmed) {
          // Check if this is the most recent barcode and can be reclaimed
          const barcodeNum = parseInt(oldBarcode);
          if (!isNaN(barcodeNum) && barcodeNum === currentBarcode - 1) {
            // Reclaim the barcode
            setCurrentBarcode(barcodeNum);
            console.log(`♻️ Reclaimed barcode ${oldBarcode}, next barcode is now ${barcodeNum}`);
          }
          updateBook('barcode', '');
        }
      } else {
        // Just update the barcode
        updateBook('barcode', newBarcode);
      }
    };
    
    const saveChanges = () => {
      // Trim array values when saving (not during typing, so spaces work)
      const cleanedBook = {
        ...editedBook,
        generalNotes: editedBook.generalNotes?.map(n => n.trim()).filter(n => n) || [],
        forFansOf: editedBook.forFansOf?.map(s => s.trim()).filter(s => s) || [],
        subjects: editedBook.subjects?.map(s => s.trim()).filter(s => s) || [],
        genres: editedBook.genres?.map(g => g.trim()).filter(g => g) || [],
      };
      const updatedBatch = [...batch];
      updatedBatch[index] = cleanedBook;
      setBatch(updatedBatch);
      setEditingBookIndex(null);
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-lg max-w-5xl w-full my-8">
          <div className="sticky top-0 bg-white border-b px-6 py-4 rounded-t-lg">
            <h2 className="text-xl font-bold">Edit Book Details</h2>
            <p className="text-sm text-gray-600">{book.title}</p>
          </div>
          
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Basic Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Barcode</label>
                  <input
                    className="w-full px-3 py-2 border rounded font-mono bg-yellow-50"
                    value={editedBook.barcode}
                    onChange={(e) => handleBarcodeChange(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editedBook.barcode && parseInt(editedBook.barcode) === currentBarcode - 1
                      ? '⚠️ Deleting this will reclaim the barcode for reuse'
                      : 'Edit or clear to change barcode'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">ISBN</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.isbn}
                    onChange={(e) => updateBook('isbn', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Title</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.title}
                    onChange={(e) => updateBook('title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Subtitle</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.subtitle}
                    onChange={(e) => updateBook('subtitle', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Author</label>
                  <input 
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.author}
                    onChange={(e) => updateBook('author', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Publisher</label>
                  <input 
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.publisher}
                    onChange={(e) => updateBook('publisher', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Publish Year</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.publishDate}
                    onChange={(e) => updateBook('publishDate', e.target.value)}
                  />
                </div>
              </div>
            </div>
            
            {/* Cataloging */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Cataloging</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <label className="text-sm font-medium block mb-1">Location</label>
                  <div className="flex flex-wrap gap-1">
                    {LOCATIONS.map(loc => (
                      <button
                        key={loc}
                        type="button"
                        onClick={() => updateBook('location', loc)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          editedBook.location === loc
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>
                {editedBook.location === 'Easy Reader' && (
                  <div>
                    <label className="text-sm font-medium block mb-1">Easy Reader Level</label>
                    <div className="flex flex-wrap gap-1">
                      {EASY_READER_SUBCATEGORIES.map(sub => (
                        <button
                          key={sub}
                          type="button"
                          onClick={() => updateBook('easyReaderSubcategory', sub)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            (editedBook.easyReaderSubcategory || 'ER1') === sub
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {sub}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {editedBook.location === 'Graphic Novels' && (
                  <div>
                    <label className="text-sm font-medium block mb-1">Spine Label</label>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => updateBook('isGN2', false)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          !editedBook.isGN2
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        GN
                      </button>
                      <button
                        type="button"
                        onClick={() => updateBook('isGN2', true)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          editedBook.isGN2
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        GN2
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium block mb-1">Fiction/Non-Fiction</label>
                  <div className="flex gap-1">
                    {['Fiction', 'Non-Fiction'].map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => updateBook('ficOrNonfic', opt)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          editedBook.ficOrNonfic === opt
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Dewey Decimal</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.deweyDecimal}
                    onChange={(e) => updateBook('deweyDecimal', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Series</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.series || ''}
                    onChange={(e) => updateBook('series', e.target.value)}
                    placeholder="Series name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Series #</label>
                  <input
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.seriesNumber || ''}
                    onChange={(e) => updateBook('seriesNumber', e.target.value)}
                    placeholder="Volume number"
                  />
                </div>
                {(editedBook.location === 'MS Quick' || editedBook.deweyDecimal?.startsWith('921') || editedBook.deweyDecimal?.startsWith('920')) && (
                  <div>
                    <label className="text-sm font-medium block mb-1">Subject Person</label>
                    <input
                      className="w-full px-3 py-2 border rounded"
                      value={editedBook.subjectPerson || ''}
                      onChange={(e) => updateBook('subjectPerson', e.target.value)}
                      placeholder="Last, First (person the book is about)"
                    />
                    <p className="text-xs text-gray-500 mt-1">For biographies: used in call number instead of author</p>
                  </div>
                )}
                <div className="col-span-3">
                  <label className="text-sm font-medium block mb-1">Call Number</label>
                  <input
                    className="w-full px-3 py-2 border rounded font-mono"
                    value={editedBook.callNumber}
                    onChange={(e) => updateBook('callNumber', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">Updates automatically when location/dewey/subject person changes, or edit manually</p>
                </div>
              </div>
            </div>

            {/* Grade & Reading Levels */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Grade & Reading Levels</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Target Audience</label>
                  <input 
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.targetAudience}
                    onChange={(e) => updateBook('targetAudience', e.target.value)}
                    placeholder="K-2, 3-5, 6-8"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Age Range</label>
                  <input 
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.ageRange}
                    onChange={(e) => updateBook('ageRange', e.target.value)}
                    placeholder="5-7 years"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Reading Level</label>
                  <input 
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.readingLevel}
                    onChange={(e) => updateBook('readingLevel', e.target.value)}
                    placeholder="3.5"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Lexile Level</label>
                  <input 
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.lexileLevel}
                    onChange={(e) => updateBook('lexileLevel', e.target.value)}
                    placeholder="500L"
                  />
                </div>
              </div>
            </div>
            
            {/* Price */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Pricing</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">List Price</label>
                  <input 
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border rounded"
                    value={editedBook.listPrice}
                    onChange={(e) => {
                      const price = parseFloat(e.target.value) || 0;
                      updateBook('listPrice', price);
                      updateBook('libraryPrice', price + 1);
                    }}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Library Price (+$1)</label>
                  <input 
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border rounded bg-gray-50"
                    value={editedBook.libraryPrice}
                    readOnly
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Price Source</label>
                  <input 
                    className="w-full px-3 py-2 border rounded bg-gray-50"
                    value={editedBook.priceSource}
                    readOnly
                  />
                </div>
              </div>
            </div>
            
            {/* Description & Notes */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Description & Notes</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Description</label>
                  <textarea 
                    className="w-full px-3 py-2 border rounded"
                    rows="3"
                    value={editedBook.description}
                    onChange={(e) => updateBook('description', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">General Notes (one per line)</label>
                  <textarea 
                    className="w-full px-3 py-2 border rounded"
                    rows="3"
                    value={editedBook.generalNotes?.join('\n') || ''}
                    onChange={(e) => {
                      const notes = e.target.value.split('\n');
                      updateBook('generalNotes', notes);
                    }}
                    placeholder="Type notes, press Enter for new line"
                  />
                  <p className="text-xs text-gray-500 mt-1">Each line becomes a separate note. Blank lines are preserved.</p>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">For Fans Of (comma-separated)</label>
                  <textarea 
                    className="w-full px-3 py-2 border rounded"
                    rows="2"
                    value={editedBook.forFansOf?.join(', ') || ''}
                    onChange={(e) => {
                      const items = e.target.value.split(',');
                      updateBook('forFansOf', items);
                    }}
                    placeholder="Similar books, authors, etc."
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate with commas. Spaces after commas are preserved.</p>
                </div>
              </div>
            </div>
            
            {/* Subjects & Genres */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Subjects & Genres</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Subjects */}
                <div>
                  <label className="text-sm font-medium block mb-2">Subjects</label>
                  <div className="space-y-2">
                    {(editedBook.subjects || ['']).map((subject, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          list={`subjects-datalist-${index}`}
                          className="flex-1 px-3 py-2 border rounded text-sm"
                          value={subject}
                          onChange={(e) => {
                            const newSubjects = [...(editedBook.subjects || [''])];
                            newSubjects[index] = e.target.value;
                            updateBook('subjects', newSubjects);
                          }}
                          placeholder="Enter subject"
                        />
                        <datalist id={`subjects-datalist-${index}`}>
                          {allSubjects.map((s, i) => (
                            <option key={i} value={s} />
                          ))}
                        </datalist>
                        {editedBook.subjects && editedBook.subjects.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newSubjects = editedBook.subjects.filter((_, i) => i !== index);
                              updateBook('subjects', newSubjects.length > 0 ? newSubjects : ['']);
                            }}
                            className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newSubjects = [...(editedBook.subjects || ['']), ''];
                        updateBook('subjects', newSubjects);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      + Add Subject
                    </button>
                  </div>
                </div>

                {/* Genres */}
                <div>
                  <label className="text-sm font-medium block mb-2">Genres</label>
                  <div className="space-y-2">
                    {(editedBook.genres || ['']).map((genre, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          list={`genres-datalist-${index}`}
                          className="flex-1 px-3 py-2 border rounded text-sm"
                          value={genre}
                          onChange={(e) => {
                            const newGenres = [...(editedBook.genres || [''])];
                            newGenres[index] = e.target.value;
                            updateBook('genres', newGenres);
                          }}
                          placeholder="Enter genre"
                        />
                        <datalist id={`genres-datalist-${index}`}>
                          {allGenres.map((g, i) => (
                            <option key={i} value={g} />
                          ))}
                        </datalist>
                        {editedBook.genres && editedBook.genres.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newGenres = editedBook.genres.filter((_, i) => i !== index);
                              updateBook('genres', newGenres.length > 0 ? newGenres : ['']);
                            }}
                            className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newGenres = [...(editedBook.genres || ['']), ''];
                        updateBook('genres', newGenres);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      + Add Genre
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Data Sources */}
            {editedBook.dataSources?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Data Sources</h3>
                <p className="text-sm text-gray-600">{editedBook.dataSources.join(', ')}</p>
              </div>
            )}
          </div>
          
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
            <button
              onClick={() => setEditingBookIndex(null)}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveChanges}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Edit Modal */}
      {editingBookIndex !== null && (
        <BookEditModal book={batch[editingBookIndex]} index={editingBookIndex} />
      )}

      {/* Re-catalog Options Modal */}
      {recatalogingBookIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Re-catalog Book</h2>
            <p className="text-sm text-gray-600 mb-6">
              Choose how to re-catalog "{batch[recatalogingBookIndex]?.title}"
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleRecatalogSearch(recatalogingBookIndex)}
                className="w-full bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 font-medium flex items-center justify-center gap-2"
              >
                <span>🔍</span>
                <span>Search Again</span>
              </button>
              <button
                onClick={() => handleRecatalogManual(recatalogingBookIndex)}
                className="w-full bg-purple-500 text-white px-4 py-3 rounded-lg hover:bg-purple-600 font-medium flex items-center justify-center gap-2"
              >
                <span>✏️</span>
                <span>Manual Entry</span>
              </button>
              <button
                onClick={() => setRecatalogingBookIndex(null)}
                className="w-full bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="text-purple-300 hidden sm:inline">|</span>
              <span className="text-purple-200 hidden sm:inline">The Forge of Odin</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right hidden md:block">
                <p className="text-sm text-purple-300 flex items-center justify-end space-x-2">
                  <span>Current Barcode</span>
                  <button
                    onClick={refreshBarcodeNow}
                    className="hover:text-purple-100 transition-colors"
                    title="Refresh barcode from database"
                  >
                    🔄
                  </button>
                </p>
                <p className="text-xl font-mono font-bold">{currentBarcode}</p>
              </div>
              <button
                onClick={handleLogout}
                className="bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Full-width Header Panel */}
      <div className="max-w-[1600px] mx-auto px-4 pt-6">
        <div className="bg-slate-200 rounded-xl shadow-xl p-4 border border-slate-300 mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-800">The Forge of Odin</h1>
              <p className="text-slate-600 text-sm">Multi-library search + AI enhancement</p>
            </div>
            {/* Navigation Links */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => navigate('/runes')} className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors text-sm">
                Labels
              </button>
              <button onClick={() => navigate('/bifrost')} className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition-colors text-sm">
                MARC
              </button>
              <button onClick={() => navigate('/loki')} className="bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors text-sm">
                Review ({flaggedCount})
              </button>
              <button onClick={() => navigate('/yggdrasil')} className="bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-sm">
                Settings
              </button>
            </div>
          </div>
        </div>

        {/* Alerts - Full Width */}
        {flaggedCount > 0 && (
          <div className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-lg mb-4">
            <strong>Content Review Required!</strong> {flaggedCount} books flagged for review.
            <button
              onClick={() => navigate('/loki')}
              className="ml-3 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
            >
              Review in Loki's Chamber
            </button>
          </div>
        )}

        {!contentFilterSettings.enableContentFiltering && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg mb-4">
            <strong>Content Filtering Disabled:</strong> All books will be added directly to batch.
            <button
              onClick={() => navigate('/yggdrasil')}
              className="ml-3 bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
            >
              Enable in Settings
            </button>
          </div>
        )}
      </div>

      {/* Main Content - Two Column Layout */}
      <main className="max-w-[1600px] mx-auto px-4 pb-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* LEFT COLUMN - Search & Input */}
          <div className="w-full lg:w-3/5">
            {/* Tabs */}
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setCatalogMode('catalog')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  catalogMode === 'catalog'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-300 text-slate-700 hover:bg-slate-400'
                }`}
              >
                📚 Catalog Books
              </button>
              <button
                onClick={() => setCatalogMode('addCopies')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  catalogMode === 'addCopies'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-300 text-slate-700 hover:bg-slate-400'
                }`}
              >
                🔄 Add Copies
              </button>
            </div>

            <div className={`bg-slate-200 rounded-xl shadow-xl p-5 border border-slate-300 space-y-3 ${catalogMode !== 'catalog' ? 'hidden' : ''}`}>

              {/* LOCATION */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setBatchMode(!batchMode)}
                      className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                        batchMode
                          ? 'bg-emerald-500 text-white ring-2 ring-emerald-300 ring-offset-1 shadow-md'
                          : 'bg-white text-gray-400 hover:text-gray-600 border border-gray-300'
                      }`}
                    >
                      {batchMode ? 'Batch Mode ON' : 'Batch Mode'}
                    </button>
                  <button
                    type="button"
                    onClick={() => {
                      setApplyLocationToAll(false);
                      setGlobalGN2(false);
                      setGlobalEasyReaderSub('ER1');
                      setIsOversized(false);
                      setApplyPhysicalFormatToAll(false);
                      setGlobalPhysicalFormat('Print');
                      setSelectedPriceTier(null);
                      setManualPrice('');
                      setShowCopyNotes(false);
                      setCopyNoteOptions({ libraryPurchase: false, donation: false, donorName: '', damage: false, bindingType: '', tla: false, tlaYear: '' });
                    }}
                    className="text-[10px] text-gray-400 hover:text-red-500 transition-colors"
                  >
                    Clear All
                  </button>
                  </div>
                </div>
                {(() => {
                  const elementaryLocs = ['Fiction', 'Non-Fiction', 'Graphic Novels', 'Easy', 'Easy Reader', 'Early Non-Fiction', 'Board Books', 'Spanish', 'Reference'];
                  const msLocs = ['MS Fiction', 'MS Non-Fiction', 'MS Graphic Novels', 'MS GN Non-Fiction', 'MS Quick'];
                  const renderLocBtn = (loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => {
                        if (applyLocationToAll && globalLocation === loc) {
                          setApplyLocationToAll(false);
                        } else {
                          setApplyLocationToAll(true);
                          setGlobalLocation(loc);
                        }
                      }}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        applyLocationToAll && globalLocation === loc
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      {loc}
                    </button>
                  );
                  return (
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-1">{elementaryLocs.map(renderLocBtn)}</div>
                      <div className="flex flex-wrap gap-1">{msLocs.map(renderLocBtn)}</div>
                    </div>
                  );
                })()}
                <div className="h-7 flex items-center mt-1">
                  {applyLocationToAll && globalLocation === 'Graphic Novels' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Spine:</span>
                      <button
                        type="button"
                        onClick={() => setGlobalGN2(false)}
                        className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                          !globalGN2
                            ? 'bg-emerald-600 text-white'
                            : 'bg-white text-emerald-700 hover:bg-emerald-50 border border-emerald-300'
                        }`}
                      >
                        GN
                      </button>
                      <button
                        type="button"
                        onClick={() => setGlobalGN2(true)}
                        className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                          globalGN2
                            ? 'bg-emerald-600 text-white'
                            : 'bg-white text-emerald-700 hover:bg-emerald-50 border border-emerald-300'
                        }`}
                      >
                        GN2
                      </button>
                    </div>
                  ) : applyLocationToAll && globalLocation === 'Easy Reader' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Level:</span>
                      {EASY_READER_SUBCATEGORIES.map(sub => (
                        <button
                          key={sub}
                          type="button"
                          onClick={() => setGlobalEasyReaderSub(sub)}
                          className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                            globalEasyReaderSub === sub
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white text-indigo-700 hover:bg-indigo-50 border border-indigo-300'
                          }`}
                        >
                          {sub}
                        </button>
                      ))}
                    </div>
                  ) : !applyLocationToAll ? (
                    <p className="text-xs text-gray-400 italic">Auto-detected per book</p>
                  ) : null}
                </div>
              </div>

              {/* FORMAT + PRICE on one line */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Format</span>
                  <div className="flex flex-wrap gap-1">
                    {[
                      { value: 'Print', label: 'Book' },
                      { value: 'Oversized', label: 'Oversized' },
                      { value: 'Audio', label: 'Audiobook' },
                      { value: 'Digital', label: 'eBook' },
                      { value: 'DVD', label: 'DVD' },
                      { value: 'Kit', label: 'Kit' }
                    ].map(fmt => {
                      const isSelected = fmt.value === 'Oversized'
                        ? isOversized
                        : (applyPhysicalFormatToAll ? globalPhysicalFormat === fmt.value : fmt.value === 'Print' && !applyPhysicalFormatToAll);
                      return (
                        <button
                          key={fmt.value}
                          type="button"
                          onClick={() => {
                            if (fmt.value === 'Oversized') {
                              setIsOversized(!isOversized);
                            } else {
                              if (applyPhysicalFormatToAll && globalPhysicalFormat === fmt.value) {
                                setApplyPhysicalFormatToAll(false);
                                setGlobalPhysicalFormat('Print');
                              } else {
                                setApplyPhysicalFormatToAll(true);
                                setGlobalPhysicalFormat(fmt.value);
                              }
                            }
                          }}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            isSelected
                              ? fmt.value === 'Oversized' ? 'bg-orange-500 text-white' : 'bg-teal-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          {fmt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Price</span>
                  <div className="flex flex-wrap items-center gap-1">
                    {[24.99, 19.99, 14.99, 9.99].map(price => (
                      <button
                        key={price}
                        type="button"
                        onClick={() => {
                          if (selectedPriceTier === price) {
                            setSelectedPriceTier(null);
                          } else {
                            setSelectedPriceTier(price);
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          selectedPriceTier === price
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        ${price}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setSelectedPriceTier(null)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        !selectedPriceTier
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      Manual
                    </button>
                    <div className={`flex items-center ml-1 transition-opacity ${!selectedPriceTier ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                      <span className="text-sm mr-1">$</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                        value={manualPrice}
                        onChange={(e) => setManualPrice(e.target.value)}
                        disabled={!!selectedPriceTier}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* COPY NOTES + AWARDS + OPAC row */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowCopyNotes(!showCopyNotes)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    showCopyNotes
                      ? 'bg-amber-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  Copy Notes
                </button>
                <label className="flex items-center space-x-1">
                  <input
                    type="checkbox"
                    checked={selectedAwards.includes('Lux Libris Award nominee')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAwards(['Lux Libris Award nominee']);
                      } else {
                        setSelectedAwards([]);
                        setLuxLibrisYear('');
                      }
                    }}
                    className="h-3.5 w-3.5 text-yellow-600"
                  />
                  <span className="text-xs text-gray-700">Lux Libris</span>
                </label>
                {selectedAwards.includes('Lux Libris Award nominee') && (
                  <input
                    type="text"
                    placeholder="Year"
                    className="px-2 py-0.5 text-xs border border-gray-300 rounded bg-white w-24"
                    value={luxLibrisYear}
                    onChange={(e) => setLuxLibrisYear(e.target.value)}
                  />
                )}
                {contentFilterSettings.enableDuplicateChecking && contentFilterSettings.opacUrl && (
                  <label className="flex items-center space-x-1">
                    <input
                      type="checkbox"
                      checked={checkDuplicatesForThisBook}
                      onChange={(e) => setCheckDuplicatesForThisBook(e.target.checked)}
                      className="h-3.5 w-3.5 text-amber-600"
                    />
                    <span className="text-xs text-amber-700">Check OPAC</span>
                  </label>
                )}
              </div>

              {/* Copy Notes expanded panel */}
              {showCopyNotes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => setCopyNoteOptions(prev => ({...prev, libraryPurchase: !prev.libraryPurchase, donation: false, donorName: ''}))}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        copyNoteOptions.libraryPurchase
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      Library Purchase
                    </button>
                    <button
                      type="button"
                      onClick={() => setCopyNoteOptions(prev => ({...prev, donation: !prev.donation, libraryPurchase: false, donorName: prev.donation ? '' : prev.donorName}))}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        copyNoteOptions.donation
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      Donation
                    </button>
                    <button
                      type="button"
                      onClick={() => setCopyNoteOptions(prev => ({...prev, damage: !prev.damage}))}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        copyNoteOptions.damage
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      Damaged
                    </button>
                    <button
                      type="button"
                      onClick={() => setCopyNoteOptions(prev => ({...prev, tla: !prev.tla, tlaYear: prev.tla ? '' : prev.tlaYear}))}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        copyNoteOptions.tla
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      TLA
                    </button>
                    <span className="text-xs text-gray-400 self-center mx-1">|</span>
                    {['Hardback', 'Paperback', 'Board Book', 'Bound'].map(bt => (
                      <button
                        key={bt}
                        type="button"
                        onClick={() => setCopyNoteOptions(prev => ({...prev, bindingType: prev.bindingType === bt ? '' : bt}))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          copyNoteOptions.bindingType === bt
                            ? 'bg-slate-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        {bt}
                      </button>
                    ))}
                  </div>
                  {copyNoteOptions.donation && (
                    <input
                      type="text"
                      placeholder="Donor name (optional)"
                      className="w-full px-2 py-1 text-sm border border-amber-300 rounded bg-white"
                      value={copyNoteOptions.donorName}
                      onChange={(e) => setCopyNoteOptions(prev => ({...prev, donorName: e.target.value}))}
                    />
                  )}
                  {copyNoteOptions.tla && (
                    <input
                      type="text"
                      placeholder="TLA year (e.g. 2026)"
                      className="w-32 px-2 py-1 text-sm border border-amber-300 rounded bg-white"
                      value={copyNoteOptions.tlaYear}
                      onChange={(e) => setCopyNoteOptions(prev => ({...prev, tlaYear: e.target.value}))}
                    />
                  )}
                </div>
              )}

              {/* Row 5: General Notes */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Note:</span>
                <input
                  type="text"
                  placeholder="Add a note for this book..."
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                />
              </div>

              {/* Row 6: Search Mode Toggle */}
              <div className="flex items-center space-x-4 pt-2 border-t border-slate-300">
                <span className="text-sm font-medium text-gray-700">Search Mode:</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSearchMode('isbn')}
                    className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-colors ${
                      searchMode === 'isbn' ? 'bg-blue-600 text-white' : 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    ISBN
                  </button>
                  <button
                    onClick={() => setSearchMode('title')}
                    className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-colors ${
                      searchMode === 'title' ? 'bg-green-600 text-white' : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
                    }`}
                  >
                    Title/Author
                  </button>
                </div>
              </div>

              {/* Row 7: Search Input Fields */}
              {searchMode === 'isbn' ? (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <input
                    type="text"
                    placeholder="Scan or enter ISBN"
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent bg-white text-lg"
                    value={currentIsbn}
                    onChange={(e) => setCurrentIsbn(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddBook()}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowScanner(true)}
                      className="bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0"
                      title="Scan barcode with camera"
                      type="button"
                    >
                      <Camera size={20} />
                    </button>
                    <button
                      onClick={handleAddBook}
                      disabled={loading || !currentIsbn.trim()}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:bg-slate-400 font-medium flex-shrink-0"
                    >
                      {loading ? 'Searching...' : 'Add'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Title Input Row */}
                  <input
                    type="text"
                    placeholder="Book Title"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent bg-white"
                    value={searchTitle}
                    onChange={(e) => setSearchTitle(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddBook()}
                  />

                  {/* Author Names Row */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <input
                      type="text"
                      placeholder="First Name"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent bg-white"
                      value={authorFirst}
                      onChange={(e) => setAuthorFirst(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddBook()}
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent bg-white"
                      value={authorLast}
                      onChange={(e) => setAuthorLast(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddBook()}
                    />
                    <button
                      onClick={handleAddBook}
                      disabled={loading || (!searchTitle.trim() && !authorFirst.trim() && !authorLast.trim())}
                      className="bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700 disabled:bg-slate-400 font-medium"
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Not Found State */}
            {notFound && !manualEntryMode && !showAmazonDetails && (
              <div className="mt-6 bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl">📚❓</div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-amber-800 mb-2">Book Not Found</h3>
                    <p className="text-amber-700 mb-4">
                      We couldn't find reliable information for this {searchMode === 'isbn' ? 'ISBN' : 'title'} in our library sources.
                      The book may be too new, out of print, or have an incorrect {searchMode === 'isbn' ? 'ISBN' : 'title/author'}.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => {
                          setNotFound(false);
                          if (searchMode === 'isbn') {
                            setCurrentIsbn('');
                          } else {
                            setSearchTitle('');
                            setAuthorFirst('');
                            setAuthorLast('');
                          }
                        }}
                        className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 font-medium"
                      >
                        Try Different Search
                      </button>
                      <button
                        onClick={() => setShowAmazonDetails(true)}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 font-medium"
                      >
                        Paste Amazon Details
                      </button>
                      <button
                        onClick={() => setManualEntryMode(true)}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 font-medium"
                      >
                        Enter Details Manually
                      </button>
                      <button
                        onClick={cancelNotFound}
                        className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Amazon Details Parsing */}
            {showAmazonDetails && (
              <div className="mt-6 bg-orange-50 border-2 border-orange-300 rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-orange-800">Paste Amazon Product Details</h3>
                  <button
                    onClick={() => {
                      setShowAmazonDetails(false);
                      setAmazonDetails('');
                    }}
                    className="text-gray-500 hover:text-gray-700 text-xl"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-orange-700 mb-3 text-sm">
                  Copy and paste the product details section from Amazon (includes title, author, publisher, ISBN, etc.)
                </p>
                <textarea
                  value={amazonDetails}
                  onChange={(e) => setAmazonDetails(e.target.value)}
                  className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
                  rows="6"
                  placeholder="Paste Amazon product details here...&#10;&#10;Example:&#10;The Very Hungry Caterpillar Board Book&#10;by Eric Carle (Author)&#10;Publisher: Philomel Books&#10;Publication date: March 23, 1994&#10;..."
                />
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowAmazonDetails(false);
                      setAmazonDetails('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (amazonDetails.trim()) {
                        handleAddBook();
                        setShowAmazonDetails(false);
                      }
                    }}
                    disabled={!amazonDetails.trim()}
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 disabled:bg-gray-400 font-medium"
                  >
                    Parse & Add to Batch
                  </button>
                </div>
              </div>
            )}

            {/* Manual Entry Form */}
            {manualEntryMode && (
              <div className="mt-6 bg-purple-50 border-2 border-purple-300 rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-purple-800">Enter Book Details Manually</h3>
                  <button
                    onClick={() => {
                      setManualEntryMode(false);
                      setNotFound(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-xl"
                  >
                    ✕
                  </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto space-y-4 mb-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">Title *</label>
                      <input
                        type="text"
                        value={manualBookData.title}
                        onChange={(e) => setManualBookData({...manualBookData, title: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Book title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">Subtitle</label>
                      <input
                        type="text"
                        value={manualBookData.subtitle}
                        onChange={(e) => setManualBookData({...manualBookData, subtitle: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Subtitle (if any)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">Author *</label>
                      <input
                        type="text"
                        value={manualBookData.author}
                        onChange={(e) => setManualBookData({...manualBookData, author: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="First Last or Last, First"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">Publisher</label>
                      <input
                        type="text"
                        value={manualBookData.publisher}
                        onChange={(e) => setManualBookData({...manualBookData, publisher: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Publisher name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">Publish Year</label>
                      <input
                        type="text"
                        value={manualBookData.publishDate}
                        onChange={(e) => setManualBookData({...manualBookData, publishDate: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="2024"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">ISBN-13</label>
                      <input
                        type="text"
                        value={manualBookData.isbn13}
                        onChange={(e) => setManualBookData({...manualBookData, isbn13: e.target.value})}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="978-0-123456-78-9"
                      />
                    </div>
                  </div>

                  {/* Cataloging Info */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-purple-800 mb-3">Cataloging</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="col-span-1 sm:col-span-2">
                        <label className="block text-sm font-medium text-purple-700 mb-1">Location *</label>
                        <div className="flex flex-wrap gap-1">
                          {LOCATIONS.map(loc => (
                            <button
                              key={loc}
                              type="button"
                              onClick={() => setManualBookData({...manualBookData, location: loc, isGN2: loc !== 'Graphic Novels' ? false : manualBookData.isGN2})}
                              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                manualBookData.location === loc
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                              }`}
                            >
                              {loc}
                            </button>
                          ))}
                        </div>
                      </div>
                      {manualBookData.location === 'Easy Reader' && (
                        <div>
                          <label className="block text-sm font-medium text-purple-700 mb-1">Easy Reader Level</label>
                          <div className="flex flex-wrap gap-1">
                            {EASY_READER_SUBCATEGORIES.map(sub => (
                              <button
                                key={sub}
                                type="button"
                                onClick={() => setManualBookData({...manualBookData, easyReaderSubcategory: sub})}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  manualBookData.easyReaderSubcategory === sub
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200'
                                }`}
                              >
                                {sub}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {manualBookData.location === 'Graphic Novels' && (
                        <div>
                          <label className="block text-sm font-medium text-purple-700 mb-1">Spine Label</label>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => setManualBookData({...manualBookData, isGN2: false})}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                !manualBookData.isGN2
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                              }`}
                            >
                              GN
                            </button>
                            <button
                              type="button"
                              onClick={() => setManualBookData({...manualBookData, isGN2: true})}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                manualBookData.isGN2
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                              }`}
                            >
                              GN2
                            </button>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Fiction/Non-Fiction *</label>
                        <div className="flex gap-1">
                          {['Fiction', 'Non-Fiction'].map(opt => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => setManualBookData({...manualBookData, ficOrNonfic: opt})}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                manualBookData.ficOrNonfic === opt
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Dewey Decimal</label>
                        <input
                          type="text"
                          value={manualBookData.deweyDecimal || ''}
                          onChange={(e) => setManualBookData({...manualBookData, deweyDecimal: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="e.g., 636.7"
                          disabled={manualBookData.ficOrNonfic === 'Fiction'}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Subjects & Genres */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-purple-800 mb-3">Subjects & Genres</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-2">Subjects</label>
                        <div className="space-y-2">
                          {(manualBookData.subjects || ['']).map((subject, index) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="text"
                                list={`manual-subjects-datalist-${index}`}
                                className="flex-1 px-3 py-2 border border-purple-300 rounded-lg text-sm"
                                value={subject}
                                onChange={(e) => {
                                  const currentSubjects = manualBookData.subjects || [''];
                                  const newSubjects = [...currentSubjects];
                                  newSubjects[index] = e.target.value;
                                  setManualBookData({...manualBookData, subjects: newSubjects});
                                }}
                                placeholder="Enter subject"
                              />
                              <datalist id={`manual-subjects-datalist-${index}`}>
                                {allSubjects.map((s, i) => <option key={i} value={s} />)}
                              </datalist>
                              {(manualBookData.subjects || ['']).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentSubjects = manualBookData.subjects || [''];
                                    const newSubjects = currentSubjects.filter((_, i) => i !== index);
                                    setManualBookData({...manualBookData, subjects: newSubjects.length > 0 ? newSubjects : ['']});
                                  }}
                                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const currentSubjects = manualBookData.subjects || [''];
                              setManualBookData({...manualBookData, subjects: [...currentSubjects, '']});
                            }}
                            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                          >
                            + Add Subject
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-2">Genres</label>
                        <div className="space-y-2">
                          {(manualBookData.genres || ['']).map((genre, index) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="text"
                                list={`manual-genres-datalist-${index}`}
                                className="flex-1 px-3 py-2 border border-purple-300 rounded-lg text-sm"
                                value={genre}
                                onChange={(e) => {
                                  const currentGenres = manualBookData.genres || [''];
                                  const newGenres = [...currentGenres];
                                  newGenres[index] = e.target.value;
                                  setManualBookData({...manualBookData, genres: newGenres});
                                }}
                                placeholder="Enter genre"
                              />
                              <datalist id={`manual-genres-datalist-${index}`}>
                                {allGenres.map((g, i) => <option key={i} value={g} />)}
                              </datalist>
                              {(manualBookData.genres || ['']).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentGenres = manualBookData.genres || [''];
                                    const newGenres = currentGenres.filter((_, i) => i !== index);
                                    setManualBookData({...manualBookData, genres: newGenres.length > 0 ? newGenres : ['']});
                                  }}
                                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const currentGenres = manualBookData.genres || [''];
                              setManualBookData({...manualBookData, genres: [...currentGenres, '']});
                            }}
                            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                          >
                            + Add Genre
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-purple-800 mb-3">Additional Details</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Pages</label>
                        <input
                          type="text"
                          value={manualBookData.pages}
                          onChange={(e) => setManualBookData({...manualBookData, pages: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="Number of pages"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Physical Format</label>
                        <select
                          value={manualBookData.physicalFormat}
                          onChange={(e) => setManualBookData({...manualBookData, physicalFormat: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          {PHYSICAL_FORMATS.map(format => (
                            <option key={format.value} value={format.value}>{format.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Target Audience</label>
                        <input
                          type="text"
                          value={manualBookData.targetAudience}
                          onChange={(e) => setManualBookData({...manualBookData, targetAudience: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="e.g., PreK-2, 2-5, 6-8"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Lexile Level</label>
                        <input
                          type="text"
                          value={manualBookData.lexileLevel}
                          onChange={(e) => setManualBookData({...manualBookData, lexileLevel: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="e.g., 500L-800L"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-purple-700 mb-1">Series</label>
                        <input
                          type="text"
                          value={manualBookData.series}
                          onChange={(e) => setManualBookData({...manualBookData, series: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="Series name (if part of a series)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Description & Notes */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-purple-800 mb-3">Description & Notes</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">Description</label>
                        <textarea
                          value={manualBookData.description}
                          onChange={(e) => setManualBookData({...manualBookData, description: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          rows="3"
                          placeholder="Brief description of the book..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-purple-700 mb-1">General Notes</label>
                        <textarea
                          value={manualBookData.generalNotes}
                          onChange={(e) => setManualBookData({...manualBookData, generalNotes: e.target.value})}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          rows="2"
                          placeholder="Any additional notes..."
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setManualEntryMode(false);
                      setNotFound(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleManualEntry}
                    disabled={!manualBookData.title.trim() || !manualBookData.author.trim()}
                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 font-medium"
                  >
                    Add to Batch
                  </button>
                </div>
              </div>
            )}

            <div className={`bg-slate-200 rounded-xl shadow-xl p-5 border border-slate-300 space-y-4 ${catalogMode !== 'addCopies' ? 'hidden' : ''}`}>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-blue-900 mb-2">🔄 Quick Copy Generator</h3>
                  <p className="text-sm text-blue-800">
                    Generate barcode labels for additional copies of existing books. No permanent records created until you process to MARC.
                  </p>
                </div>

                {/* Input Form */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Call Number *</label>
                    <input
                      type="text"
                      list="call-numbers-datalist"
                      value={copyCallNumber}
                      onChange={(e) => handleCallNumberChange(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., FIC ABC"
                    />
                    <datalist id="call-numbers-datalist">
                      {allCallNumbers.map((item, i) => (
                        <option key={i} value={item.callNumber}>{item.title}</option>
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={copyTitle}
                      onChange={(e) => setCopyTitle(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Book title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Copies</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={copyCount}
                      onChange={(e) => setCopyCount(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Next Barcode: <span className="font-mono font-bold text-indigo-600">{currentBarcode}</span>
                  </div>
                  <button
                    onClick={handleAddCopies}
                    disabled={!copyCallNumber.trim() || !copyTitle.trim()}
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 font-medium"
                  >
                    Add to Batch
                  </button>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <h4 className="font-semibold text-blue-900 mb-2">📋 How it works</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Enter a call number (autocomplete will suggest existing books)</li>
                    <li>• Title auto-fills if call number is found in your catalog</li>
                    <li>• Specify number of copies and add to batch</li>
                    <li>• Print labels from the batch on the right</li>
                    <li>• Copies are marked as "copy-only" and won't generate MARC records</li>
                  </ul>
                </div>
            </div>
          </div>
          {/* END LEFT COLUMN */}

          {/* RIGHT COLUMN - Batch List */}
          <div className="w-full lg:w-2/5">
            <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300 lg:sticky lg:top-6 flex flex-col max-h-[60vh] lg:max-h-[calc(100vh-140px)]">
              {/* Header */}
              <div className="px-4 py-3 border-b border-slate-300 bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl flex-shrink-0">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-slate-800">
                    Batch {batch.length > 0 && <span className="text-purple-600">({batch.length})</span>}
                  </h2>
                  {batch.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={processBatch}
                        disabled={loading}
                        className="bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:bg-slate-400 text-sm font-medium"
                      >
                        Process
                      </button>
                      <button
                        onClick={() => {
                          const resetBarcode = userData?.lastBarcodeUsed ? userData.lastBarcodeUsed + 1 : 125864;
                          setBatch([]);
                          setCurrentBarcode(resetBarcode);
                          clearBatchStorage();
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                        title="Clear all"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Book List */}
              <div className="flex-1 overflow-y-auto">
                {batch.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <div className="text-4xl mb-3">📚</div>
                    <p className="font-medium">No books in batch</p>
                    <p className="text-sm mt-1">Scan an ISBN or search by title to add books</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-300">
                    {batch.map((book, index) => (
                      <div key={index} className="px-3 py-2 hover:bg-slate-150">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-mono text-xs font-bold ${book.barcode.startsWith('PENDING-') ? 'text-amber-600' : 'text-slate-600'}`}>
                                {book.barcode.startsWith('PENDING-') ? '⏳' : book.barcode}
                              </span>
                              <span className="text-green-700 font-semibold text-xs">${book.libraryPrice?.toFixed(2)}</span>
                            </div>
                            <div className="font-medium text-sm truncate" title={book.title}>{book.title}</div>
                            <div className="text-xs text-gray-600 truncate">{book.author}</div>
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              <select
                                value={book.location}
                                onChange={(e) => {
                                  const newLocation = e.target.value;
                                  const updatedBatch = [...batch];
                                  const newSubcategory = newLocation === 'Easy Reader' ? (book.easyReaderSubcategory || 'ER1') : book.easyReaderSubcategory;
                                  updatedBatch[index] = {
                                    ...book,
                                    location: newLocation,
                                    easyReaderSubcategory: newSubcategory,
                                    isGN2: newLocation === 'Graphic Novels' ? book.isGN2 : false,
                                    callNumber: generateCallNumber(newLocation, book.deweyDecimal, book.author, book.isOversized, newSubcategory, book.subjectPerson, newLocation === 'Graphic Novels' ? book.isGN2 : false)
                                  };
                                  setBatch(updatedBatch);
                                }}
                                className="text-purple-700 font-medium bg-purple-50 border border-purple-300 rounded px-1.5 py-0.5 text-xs"
                              >
                                {LOCATIONS.map(loc => (
                                  <option key={loc} value={loc}>{loc}</option>
                                ))}
                              </select>
                              {book.location === 'Easy Reader' && (
                                <select
                                  value={book.easyReaderSubcategory || 'ER1'}
                                  onChange={(e) => {
                                    const newSubcategory = e.target.value;
                                    const updatedBatch = [...batch];
                                    updatedBatch[index] = {
                                      ...book,
                                      easyReaderSubcategory: newSubcategory,
                                      callNumber: generateCallNumber(book.location, book.deweyDecimal, book.author, book.isOversized, newSubcategory, book.subjectPerson, book.isGN2)
                                    };
                                    setBatch(updatedBatch);
                                  }}
                                  className="text-indigo-700 font-medium bg-indigo-50 border border-indigo-300 rounded px-1.5 py-0.5 text-xs"
                                >
                                  {EASY_READER_SUBCATEGORIES.map(sub => (
                                    <option key={sub} value={sub}>{sub}</option>
                                  ))}
                                </select>
                              )}
                              {book.location === 'Graphic Novels' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newGN2 = !book.isGN2;
                                    const updatedBatch = [...batch];
                                    updatedBatch[index] = {
                                      ...book,
                                      isGN2: newGN2,
                                      callNumber: generateCallNumber(book.location, book.deweyDecimal, book.author, book.isOversized, book.easyReaderSubcategory, book.subjectPerson, newGN2)
                                    };
                                    setBatch(updatedBatch);
                                  }}
                                  className={`font-medium rounded px-1.5 py-0.5 text-xs transition-colors ${
                                    book.isGN2
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100'
                                  }`}
                                >
                                  GN2
                                </button>
                              )}
                              <span className="text-blue-700 font-mono bg-blue-50 px-1.5 py-0.5 rounded text-xs">{book.callNumber}</span>
                              {book.isOversized && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">OS</span>}
                            </div>
                            {book.series && (
                              <div className="text-xs text-indigo-600 mt-0.5">
                                {book.series}{book.seriesNumber ? ` #${book.seriesNumber}` : ''}
                              </div>
                            )}
                            {book.subjectPerson && (
                              <div className="text-xs text-teal-600 mt-0.5">Subject: {book.subjectPerson}</div>
                            )}
                            {book.aiSuggestedLocation && book.aiSuggestedLocation !== book.location && (
                              <div className="text-xs text-amber-600 mt-0.5">AI suggests: {book.aiSuggestedLocation}</div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={() => setEditingBookIndex(index)}
                              className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs hover:bg-blue-600"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleAskAI(index)}
                              disabled={enrichingIndex === index}
                              className={`${
                                book.aiEnriched
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-purple-500 text-white hover:bg-purple-600'
                              } px-2 py-0.5 rounded text-xs disabled:opacity-50`}
                              title={book.aiEnriched ? 'AI enrichment applied (click to re-run)' : 'Get AI enrichment (Dewey, series, description)'}
                            >
                              {enrichingIndex === index ? '...' : book.aiEnriched ? 'AI' : 'Ask AI'}
                            </button>
                            <button
                              onClick={() => setRecatalogingBookIndex(index)}
                              className="bg-amber-500 text-white px-2 py-0.5 rounded text-xs hover:bg-amber-600"
                              title="Re-catalog this book"
                            >
                              Re-catalog
                            </button>
                            <button
                              onClick={() => removeBookFromBatch(index)}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={batchEndRef} />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* END RIGHT COLUMN */}
        </div>
      </main>

      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        continuous={true}
      />
    </div>
  );
}

export default ForgeOfOdin;